"use client";

import Image from "next/image";
import Link, { useLinkStatus } from "next/link";
import { usePathname } from "next/navigation";
import type { CSSProperties, MouseEvent } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  AUTH_CHANGE_EVENT,
  ensureFreshAuthSession,
  readAuthSession,
  writeAuthSession,
  type AuthSession,
} from "@/lib/auth-session";
import { useCustomerLogout } from "@/lib/use-customer-logout";
import { productSearchDestination } from "@/lib/product-link";
import { LOCALE_PREFERENCES_CHANGE_EVENT, LOCALE_PREFERENCES_STORAGE_KEY, readLocalePreferences, saveLocalePreferences } from "@/lib/locale-preferences";
import { MobileAccountDrawer } from "@/components/prv/mobile-account-drawer";
import { MobileProductSearch } from "@/components/prv/mobile-product-search";
import { getSocialLink, isPortalNavItemActive, portalNav } from "@/components/prv/data";
import accountNavStyles from "@/components/prv/account-navigation.module.css";
import {
  ArrowRight,
  ChevronDown,
  ExternalLink,
  Globe2,
  Headphones,
  Link2,
  LoaderCircle,
  Menu,
  Minus,
  Play,
  Plus,
  ShoppingBag,
  X,
} from "lucide-react";

function AccountMenuLinkContent({ label, comingSoon }: { label: string; comingSoon: boolean }) {
  const { pending } = useLinkStatus();

  return (
    <span className={accountNavStyles.itemLabel} data-navigation-pending={pending ? "true" : undefined}>
      <span>{label}</span>
      <span
        className={pending ? accountNavStyles.linkPending : accountNavStyles.linkPendingIdle}
        role={pending ? "status" : undefined}
        aria-live={pending ? "polite" : undefined}
      >
        {pending ? (
          <>
            <LoaderCircle aria-hidden="true" />
            Opening…
          </>
        ) : comingSoon ? (
          <span className={accountNavStyles.badge}>Coming Soon</span>
        ) : null}
      </span>
    </span>
  );
}

const navItems = [
  { label: "Shipping Estimate", href: "/shipping-estimate" },
  { label: "DIY Order", href: "/diy-order" },
  { label: "Affiliate", href: "/affiliate" },
  { label: "Help Center", href: "/help-center" },
];

const supportedLocale = { language: "EN", currency: "USD" } as const;

function localeSnapshot() {
  const value = readLocalePreferences();
  return `${value.language}|${value.currency}`;
}

function subscribeLocale(onChange: () => void) {
  const storage = (event: StorageEvent) => { if (event.key === LOCALE_PREFERENCES_STORAGE_KEY) onChange(); };
  window.addEventListener("storage", storage);
  window.addEventListener(LOCALE_PREFERENCES_CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", storage);
    window.removeEventListener(LOCALE_PREFERENCES_CHANGE_EVENT, onChange);
  };
}

const designedLanguages = [
  { code: "EN", label: "English", available: true },
  { code: "IT", label: "Italiano", available: false },
  { code: "FR", label: "Français", available: false },
  { code: "CS", label: "Čeština", available: false },
  { code: "DE", label: "Deutsch", available: false },
  { code: "RO", label: "Română", available: false },
  { code: "ES", label: "Español", available: false },
  { code: "PT", label: "Português", available: false },
  { code: "SV", label: "Svenska", available: false },
  { code: "PL", label: "Polski", available: false },
  { code: "CN", label: "中文", available: true },
] as const;

const designedCurrencies = ["USD", "EUR", "GBP", "CZK", "CAD", "AUD", "NZD", "PLN", "CHF", "CNY"] as const;

type HeaderCartItem = {
  id: string;
  productId: string;
  skuId: string;
  title: string;
  sellerName: string;
  platform?: string;
  skuLabel: string;
  quantity: number;
  image: string;
  unitPrice: { currency: string; amount: number };
  domesticShipping: { currency: string; amount: number };
};

function formatCartMoney(currency: string, amount: number) {
  return `${currency} ${amount.toFixed(2)}`;
}

function summarizeCartItems(items: HeaderCartItem[]) {
  const totals = new Map<string, number>();
  items.forEach((item) => {
    const currency = item.unitPrice.currency || "CNY";
    totals.set(currency, (totals.get(currency) ?? 0) + item.unitPrice.amount * item.quantity);
  });
  return Array.from(totals, ([currency, amount]) => formatCartMoney(currency, amount)).join(" · ");
}

const discordLink = getSocialLink("Discord");
const redditLink = getSocialLink("Reddit");
const youtubeLink = getSocialLink("YouTube");

const morphicHeaderLockCss = `
body > header.cd-header.home-shared-header,
body > header.cd-header.product-home-header {
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100vw !important;
  height: 72px !important;
  min-height: 72px !important;
  padding: 0 !important;
  gap: 0 !important;
  row-gap: 0 !important;
  column-gap: 0 !important;
  overflow: visible !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

body > header.cd-header.home-shared-header::before,
body > header.cd-header.home-shared-header::after,
body > header.cd-header.product-home-header::before,
body > header.cd-header.product-home-header::after,
body > header.cd-header.home-shared-header nav.cd-nav.cd-nav::before,
body > header.cd-header.home-shared-header nav.cd-nav.cd-nav::after,
body > header.cd-header.product-home-header nav.cd-nav.cd-nav::before,
body > header.cd-header.product-home-header nav.cd-nav.cd-nav::after {
  content: none !important;
  display: none !important;
}

body > header.cd-header.home-shared-header .current-morphic-nav-blur.current-morphic-nav-blur,
body > header.cd-header.product-home-header .current-morphic-nav-blur.current-morphic-nav-blur {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 0 !important;
  width: 100vw !important;
  height: 120px !important;
  pointer-events: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  background:
    radial-gradient(circle at 14% 0%, rgba(255, 194, 26, 0.08), transparent 30%),
    radial-gradient(circle at 63% 0%, rgba(255, 194, 26, 0.05), transparent 32%) !important;
  box-shadow: none !important;
  filter: none !important;
  opacity: 1 !important;
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  mask-image: linear-gradient(180deg, #000 40%, transparent 100%) !important;
  -webkit-mask-image: linear-gradient(180deg, #000 40%, transparent 100%) !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header .current-morphic-nav-blur.current-morphic-nav-blur.current-morphic-nav-blur,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.home-shared-header.home-shared-header .current-morphic-nav-blur.current-morphic-nav-blur.current-morphic-nav-blur,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.product-home-header.product-home-header .current-morphic-nav-blur.current-morphic-nav-blur.current-morphic-nav-blur {
  height: 120px !important;
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  mask-image: linear-gradient(180deg, #000 40%, transparent 100%) !important;
  -webkit-mask-image: linear-gradient(180deg, #000 40%, transparent 100%) !important;
}

body > header.cd-header.home-shared-header nav.cd-nav.cd-nav,
body > header.cd-header.product-home-header nav.cd-nav.cd-nav {
  position: relative !important;
  z-index: 2 !important;
  width: 100vw !important;
  max-width: none !important;
  height: 72px !important;
  min-height: 72px !important;
  margin: 0 !important;
  padding: 0 32px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%) !important;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.product-home-header.product-home-header nav.cd-nav.cd-nav.cd-nav.cd-nav {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%) !important;
  box-shadow: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav::before,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav::before,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.product-home-header.product-home-header nav.cd-nav.cd-nav.cd-nav.cd-nav::before,
html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav::after,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav::after,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.product-home-header.product-home-header nav.cd-nav.cd-nav.cd-nav.cd-nav::after {
  content: none !important;
  display: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav :is(.locale-picker, .auth-pill-signed-in, .shopping-bag-trigger) {
  height: 36px !important;
  min-height: 36px !important;
  margin: 0 !important;
  padding: 0 !important;
  align-items: center !important;
  align-self: center !important;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%) !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .locale-picker {
  margin-right: 18px !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .auth-pill-signed-in {
  margin-right: 0 !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .shopping-bag-trigger {
  margin-right: 42px !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav details.locale-picker > summary.region-pill.region-pill.region-pill,
html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .account-avatar-button.account-avatar-button.account-avatar-button,
html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .shopping-bag-trigger > button.cart-pill.cart-pill.cart-pill {
  display: inline-flex !important;
  position: relative !important;
  width: auto !important;
  min-width: 36px !important;
  max-width: none !important;
  height: 36px !important;
  min-height: 36px !important;
  margin: 0 !important;
  padding: 0 6px !important;
  align-items: center !important;
  justify-content: center !important;
  color: rgba(247, 248, 244, 0.82) !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 36px !important;
  background: transparent !important;
  background-image: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav details.locale-picker > summary.region-pill.region-pill.region-pill > * {
  display: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav details.locale-picker > summary.region-pill.region-pill.region-pill::before {
  content: "A$" !important;
  position: static !important;
  display: inline !important;
  width: auto !important;
  height: auto !important;
  color: inherit !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  letter-spacing: -0.01em !important;
  transform: none !important;
  filter: none !important;
  background: none !important;
  border: 0 !important;
  box-shadow: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .locale-popover.locale-popover {
  top: 48px !important;
  right: 0 !important;
  left: auto !important;
  width: min(468px, calc(100vw - 40px)) !important;
  min-width: 420px !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .account-dropdown.account-dropdown {
  top: 48px !important;
  right: -76px !important;
  left: auto !important;
  width: 320px !important;
  min-width: 320px !important;
  opacity: 1 !important;
  visibility: visible !important;
  transform: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .header-link-entry.header-link-entry {
  flex: 0 1 230px !important;
  width: 230px !important;
  min-width: 190px !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .nav-pill.nav-pill {
  width: auto !important;
  margin-left: 12px !important;
  gap: 26px !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .nav-pill.nav-pill > a {
  flex: 0 0 auto !important;
  width: auto !important;
  min-width: 0 !important;
  padding-inline: 2px !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav details.locale-picker > summary.region-pill.region-pill.region-pill::after {
  content: none !important;
  display: none !important;
}

html:has(.hero-section) body:has(.hero-section):not(:has(.product-page-v9)) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav .account-avatar-button .user-avatar {
  width: auto !important;
  height: auto !important;
  color: inherit !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  background: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.home-shared-header.home-shared-header nav.cd-nav.cd-nav.cd-nav.cd-nav,
html:has(.product-page-v9) body:has(.product-page-v9) > header.cd-header.product-home-header.product-home-header nav.cd-nav.cd-nav.cd-nav.cd-nav {
  background: transparent !important;
  box-shadow: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

/* Homepage utility controls and menus are the global source of truth.
   The ID prevents route-level portal/tool CSS from resizing or recoloring them. */
html body > #prvbuy-global-header.cd-header.home-shared-header .region-pill,
html body > #prvbuy-global-header.cd-header.home-shared-header .account-avatar-button,
html body > #prvbuy-global-header.cd-header.home-shared-header .cart-pill {
  position: relative !important;
  display: inline-flex !important;
  width: 36px !important;
  min-width: 36px !important;
  max-width: 36px !important;
  height: 36px !important;
  min-height: 36px !important;
  max-height: 36px !important;
  padding: 0 !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  color: rgba(247, 248, 244, 0.82) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .region-pill > * {
  display: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .region-pill::before {
  position: static !important;
  display: inline !important;
  width: auto !important;
  height: auto !important;
  content: "A$" !important;
  border: 0 !important;
  background: none !important;
  box-shadow: none !important;
  color: inherit !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  letter-spacing: -0.01em !important;
  transform: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .region-pill::after {
  display: none !important;
  content: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .account-avatar-button .user-avatar {
  display: inline-flex !important;
  width: auto !important;
  height: auto !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: none !important;
  box-shadow: none !important;
  color: inherit !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .shopping-bag-trigger,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.auth-pill-signed-in,
html body > #prvbuy-global-header.cd-header.home-shared-header .locale-picker {
  background: transparent !important;
  background-image: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .cart-pill svg {
  filter: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .shopping-bag-count {
  position: absolute !important;
  top: -4px !important;
  right: -5px !important;
  display: grid !important;
  min-width: 17px !important;
  height: 17px !important;
  padding: 0 4px !important;
  place-items: center !important;
  border: 1px solid rgba(5, 6, 5, 0.88) !important;
  border-radius: 999px !important;
  background: #ffc21a !important;
  color: #11120f !important;
  font-size: 9px !important;
  font-weight: 820 !important;
  line-height: 1 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.auth-pill-signed-in {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.auth-pill-signed-in > .account-avatar-button.account-avatar-button {
  position: relative !important;
  inset: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  align-self: center !important;
  justify-self: center !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.auth-pill-signed-in > .account-avatar-button.account-avatar-button > .user-avatar.user-avatar {
  display: inline-flex !important;
  width: auto !important;
  height: auto !important;
  min-width: 0 !important;
  min-height: 0 !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 1 !important;
  vertical-align: middle !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header :is(.locale-popover, .account-dropdown) {
  z-index: 240 !important;
  border: 1px solid rgba(247, 247, 242, 0.18) !important;
  border-radius: 24px !important;
  background:
    radial-gradient(circle at 28% 36%, rgba(255, 255, 255, 0.12), transparent 28%),
    linear-gradient(180deg, rgba(35, 38, 37, 0.94), rgba(13, 15, 14, 0.92)) !important;
  background-color: rgba(22, 24, 23, 0.94) !important;
  box-shadow:
    0 26px 72px rgba(0, 0, 0, 0.52),
    inset 0 1px 0 rgba(255, 255, 255, 0.13) !important;
  color: #f7f7f2 !important;
  backdrop-filter: blur(28px) saturate(1.16) !important;
  -webkit-backdrop-filter: blur(28px) saturate(1.16) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover {
  top: 48px !important;
  right: 0 !important;
  left: auto !important;
  width: min(380px, calc(100vw - 40px)) !important;
  min-width: 320px !important;
  height: auto !important;
  max-height: none !important;
  padding: 18px !important;
  overflow: visible !important;
  transform: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-arrow {
  top: -9px !important;
  right: 16px !important;
  left: auto !important;
  width: 18px !important;
  height: 18px !important;
  border-color: rgba(247, 247, 242, 0.18) !important;
  background: rgba(35, 38, 37, 0.96) !important;
  transform: rotate(45deg) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-heading {
  margin-bottom: 8px !important;
  color: rgba(247, 247, 242, 0.78) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-section + .locale-section {
  margin-top: 12px !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-grid {
  gap: 7px 10px !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option {
  min-height: 36px !important;
  height: 36px !important;
  padding: 0 10px !important;
  border-color: rgba(247, 247, 242, 0.13) !important;
  background: rgba(255, 255, 255, 0.055) !important;
  color: rgba(247, 247, 242, 0.86) !important;
  font-size: 14px !important;
  box-shadow: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option:hover {
  border-color: rgba(255, 194, 26, 0.34) !important;
  background: rgba(255, 194, 26, 0.09) !important;
  color: #fff !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option.active {
  border-color: rgba(143, 178, 255, 0.28) !important;
  background:
    linear-gradient(180deg, rgba(51, 61, 79, 0.86), rgba(32, 39, 52, 0.82)) !important;
  color: #fff !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option.unavailable:disabled {
  cursor: not-allowed !important;
  opacity: 0.58 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown {
  top: 48px !important;
  right: auto !important;
  left: 50% !important;
  width: 280px !important;
  min-width: 280px !important;
  height: auto !important;
  max-height: none !important;
  padding: 20px !important;
  overflow: visible !important;
  opacity: 1 !important;
  visibility: visible !important;
  transform: translateX(-54%) !important;
  pointer-events: auto !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown :is(a, button) {
  pointer-events: auto !important;
}

html body #prvbuy-global-header.cd-header.home-shared-header :is(
  .account-hover-bridge,
  .locale-hover-bridge
) {
  display: none;
}

@media (min-width: 761px) {
  html body #prvbuy-global-header.cd-header.home-shared-header :is(
    .account-dropdown > .account-hover-bridge,
    .locale-popover > .locale-hover-bridge
  ) {
    position: absolute !important;
    top: -16px !important;
    right: 0 !important;
    left: 0 !important;
    display: block !important;
    box-sizing: border-box !important;
    width: 100% !important;
    height: 16px !important;
    background: transparent !important;
    pointer-events: auto !important;
  }
}

html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown :is(
  a,
  button,
  span,
  strong,
  div
) {
  color: #f7f7f2 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links > a,
html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-signout {
  height: 36px !important;
  min-height: 36px !important;
  padding: 0 12px !important;
  border-radius: 12px !important;
  background: transparent !important;
  color: rgba(247, 247, 242, 0.76) !important;
  font-size: 15px !important;
  font-weight: 760 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links > a:hover,
html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-signout:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in) {
  position: relative !important;
  display: grid !important;
  grid-template-columns: repeat(2, 1fr) !important;
  flex: 0 0 142px !important;
  width: 142px !important;
  min-width: 142px !important;
  max-width: 142px !important;
  height: 72px !important;
  min-height: 72px !important;
  padding: 0 !important;
  gap: 0 !important;
  row-gap: 0 !important;
  column-gap: 0 !important;
  overflow: visible !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  isolation: isolate !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in)::before,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in)::after {
  position: absolute !important;
  top: 14px !important;
  left: 0 !important;
  display: block !important;
  width: 71px !important;
  height: 44px !important;
  content: "" !important;
  pointer-events: none !important;
  border: 0 !important;
  border-radius: 999px !important;
  transform: translateX(71px) !important;
  transition:
    transform 420ms cubic-bezier(0.22, 1, 0.36, 1),
    border-radius 420ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 420ms ease !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in)::before {
  z-index: -1 !important;
  background:
    radial-gradient(circle at 28% 18%, rgba(255, 255, 255, 0.92), transparent 34%),
    linear-gradient(180deg, #fff, #f0eee7) !important;
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.2),
    0 0 28px rgba(255, 194, 26, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in)::after {
  z-index: -2 !important;
  background: rgba(255, 194, 26, 0.26) !important;
  filter: blur(13px) !important;
  opacity: 0.72 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:hover)::before,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:focus-visible)::before,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:hover)::after,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:focus-visible)::after {
  border-radius: 42% 58% 55% 45% / 54% 46% 54% 46% !important;
  transform: translateX(0) scaleX(1.05) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:last-child:hover)::before,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:last-child:focus-visible)::before,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:last-child:hover)::after,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:last-child:focus-visible)::after {
  border-radius: 58% 42% 45% 55% / 46% 54% 46% 54% !important;
  transform: translateX(71px) scaleX(1.05) !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in) > a {
  position: relative !important;
  z-index: 1 !important;
  display: inline-flex !important;
  width: 71px !important;
  min-width: 0 !important;
  max-width: 71px !important;
  height: 72px !important;
  padding: 0 !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
  color: rgba(247, 248, 244, 0.74) !important;
  font-size: 14px !important;
  font-weight: 560 !important;
  white-space: nowrap !important;
  transition: color 240ms ease, transform 240ms ease !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in) > a:is(:hover, :focus-visible) {
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in) > a:last-child,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:hover) > a:first-child,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:focus-visible) > a:first-child {
  color: #11120f !important;
  font-weight: 760 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:hover) > a:last-child,
html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in):has(a:first-child:focus-visible) > a:last-child {
  color: rgba(247, 248, 244, 0.74) !important;
  font-weight: 560 !important;
}

html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.liquid-pill:not(.auth-pill-signed-in) > a:active {
  transform: scale(0.96) !important;
}

@media (min-width: 761px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav {
    display: flex !important;
    align-items: center !important;
    gap: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill {
    order: 1 !important;
    flex: 0 0 240px !important;
    width: 240px !important;
    margin: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry {
    order: 2 !important;
    flex: 0 1 230px !important;
    width: 230px !important;
    min-width: 190px !important;
    margin: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill {
    order: 3 !important;
    flex: 1 1 auto !important;
    width: auto !important;
    min-width: 0 !important;
    margin: 0 0 0 12px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .locale-picker {
    order: 4 !important;
    flex: 0 0 48px !important;
    width: 48px !important;
    min-width: 48px !important;
    height: 36px !important;
    margin: 0 18px 0 0 !important;
    align-self: center !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .auth-pill {
    order: 5 !important;
    margin: 0 !important;
    align-self: center !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .auth-pill.auth-pill-signed-in {
    flex: 0 0 48px !important;
    width: 48px !important;
    min-width: 48px !important;
    max-width: 48px !important;
    height: 36px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .shopping-bag-trigger {
    order: 6 !important;
    display: grid !important;
    flex: 0 0 48px !important;
    width: 48px !important;
    min-width: 48px !important;
    height: 36px !important;
    margin: 0 42px 0 0 !important;
    place-items: center !important;
    align-self: center !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .menu-pill {
    order: 7 !important;
    display: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav {
    display: grid !important;
    grid-template-columns: 160px 190px minmax(0, 1fr) auto !important;
    column-gap: 24px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill {
    grid-column: 1 !important;
    width: 160px !important;
    min-width: 160px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry {
    grid-column: 2 !important;
    width: 190px !important;
    min-width: 190px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill {
    grid-column: 3 !important;
    display: flex !important;
    width: 100% !important;
    min-width: 0 !important;
    margin: 0 !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 26px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools {
    grid-column: 4 !important;
    order: 4 !important;
    display: flex !important;
    min-width: 0 !important;
    padding-right: 42px !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 12px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools
    > :is(.locale-picker, .auth-pill, .shopping-bag-trigger) {
    margin: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools
    > .shopping-bag-trigger {
    display: grid !important;
    width: 48px !important;
    min-width: 48px !important;
    height: 36px !important;
    place-items: center !important;
  }

  @media (min-width: 1400px) {
    html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav {
      display: flex !important;
      gap: 0 !important;
    }

    html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill {
      flex: 0 0 240px !important;
      width: 240px !important;
      min-width: 240px !important;
    }

    html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry {
      flex: 0 0 200px !important;
      width: 200px !important;
      min-width: 200px !important;
    }

    html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill {
      position: absolute !important;
      left: 50% !important;
      width: auto !important;
      transform: translateX(-50%) !important;
    }

    html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools {
      margin-left: auto !important;
    }
  }
}

@media (min-width: 1100px) and (max-width: 1399px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav {
    display: flex !important;
    gap: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill {
    flex: 0 0 160px !important;
    width: 160px !important;
    min-width: 160px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry {
    flex: 0 0 156px !important;
    width: 156px !important;
    min-width: 156px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill {
    position: absolute !important;
    left: 50% !important;
    width: auto !important;
    transform: translateX(-50%) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools {
    margin-left: auto !important;
  }
}

@media (max-width: 760px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header {
    width: 100vw !important;
    height: 60px !important;
    min-height: 60px !important;
    overflow: visible !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header nav.cd-nav {
    display: flex !important;
    width: 100vw !important;
    height: 60px !important;
    min-height: 60px !important;
    padding: 0 14px !important;
    align-items: center !important;
    gap: 0 !important;
    overflow: visible !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .brand-pill {
    order: 0 !important;
    display: flex !important;
    flex: 0 0 42px !important;
    width: 42px !important;
    height: 60px !important;
    min-width: 42px !important;
    margin: 0 auto 0 0 !important;
    padding: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .brand-pill > span:last-child {
    display: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header :is(
    .header-link-entry,
    .locale-picker,
    .nav-pill
  ) {
    display: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .header-tools {
    display: contents !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .header-tools > .auth-pill {
    display: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .shopping-bag-trigger {
    order: 2 !important;
    display: grid !important;
    width: 48px !important;
    min-width: 48px !important;
    height: 48px !important;
    margin: 0 !important;
    place-items: center !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill {
    order: 1 !important;
    display: flex !important;
    flex: 0 0 auto !important;
    width: auto !important;
    height: 48px !important;
    min-width: 0 !important;
    margin: 0 !important;
    align-items: center !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill.auth-pill-signed-in {
    width: 48px !important;
    justify-content: center !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown {
    top: 48px !important;
    right: 0 !important;
    left: auto !important;
    width: min(280px, calc(100vw - 32px)) !important;
    min-width: min(280px, calc(100vw - 32px)) !important;
    max-height: calc(100dvh - 84px) !important;
    overflow-y: auto !important;
    transform: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .menu-pill {
    order: 2 !important;
    display: inline-flex !important;
    flex: 0 0 44px !important;
    width: 44px !important;
    height: 44px !important;
    min-width: 44px !important;
    margin: 0 0 0 12px !important;
    padding: 0 !important;
    align-items: center !important;
    justify-content: center !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    color: #f8f7ef !important;
    box-shadow: none !important;
    transition:
      color 180ms ease,
      transform 180ms ease !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .menu-pill:active {
    color: #ffc21a !important;
    transform: scale(0.94) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu {
    position: fixed !important;
    inset: 60px auto 0 0 !important;
    z-index: 2200 !important;
    display: grid !important;
    grid-template-rows: auto minmax(0, 1fr) auto !important;
    width: min(88vw, 348px) !important;
    max-width: calc(100vw - 44px) !important;
    height: calc(100dvh - 60px) !important;
    min-height: 0 !important;
    padding:
      18px 14px
      max(20px, env(safe-area-inset-bottom)) !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    border: 0 !important;
    border-top: 1px solid rgba(248, 247, 239, 0.1) !important;
    border-radius: 0 !important;
    background: #050706 !important;
    color: #f8f7ef !important;
    box-shadow: 28px 0 70px rgba(0, 0, 0, 0.52), 100vw 0 0 rgba(0, 0, 0, 0.58) !important;
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
    transform: translateX(-100%) !important;
    transition:
      opacity 220ms ease,
      visibility 0s linear 220ms,
      transform 280ms cubic-bezier(0.22, 1, 0.36, 1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu.open {
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    transform: translateX(0) !important;
    transition:
      opacity 220ms ease,
      visibility 0s linear 0s,
      transform 280ms cubic-bezier(0.22, 1, 0.36, 1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-head {
    display: flex !important;
    min-height: 54px !important;
    padding: 0 0 18px !important;
    align-items: flex-end !important;
    justify-content: space-between !important;
    gap: 20px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-head > span {
    color: #ffc21a !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    line-height: 1 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-head > small {
    color: rgba(248, 247, 239, 0.42) !important;
    font-size: 11px !important;
    font-weight: 560 !important;
    line-height: 1 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links {
    display: grid !important;
    align-content: start !important;
    width: 100% !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links > a {
    display: grid !important;
    grid-template-columns: 34px minmax(0, 1fr) 24px !important;
    width: 100% !important;
    min-height: 64px !important;
    padding: 0 !important;
    align-items: center !important;
    gap: 12px !important;
    border: 0 !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1) !important;
    border-radius: 0 !important;
    color: #f8f7ef !important;
    font-size: clamp(20px, 5.8vw, 24px) !important;
    font-weight: 650 !important;
    line-height: 1.1 !important;
    letter-spacing: -0.025em !important;
    text-decoration: none !important;
    transition:
      color 180ms ease,
      transform 180ms ease !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links > a > svg {
    color: rgba(248, 247, 239, 0.32) !important;
    justify-self: end !important;
    transition:
      color 180ms ease,
      transform 180ms ease !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-index {
    color: rgba(248, 247, 239, 0.36) !important;
    font-size: 10px !important;
    font-weight: 780 !important;
    letter-spacing: 0.08em !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links > a.active {
    color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links > a:active {
    color: #ffc21a !important;
    transform: translateX(3px) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links > a:active > svg {
    color: #ffc21a !important;
    transform: translateX(2px) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-primary {
    display: flex !important;
    width: 100% !important;
    min-height: 54px !important;
    margin-top: 20px !important;
    padding: 0 18px !important;
    align-items: center !important;
    justify-content: space-between !important;
    border: 1px solid #ffc21a !important;
    border-radius: 6px !important;
    background: #ffc21a !important;
    color: #080a09 !important;
    font-size: 15px !important;
    font-weight: 800 !important;
    line-height: 1 !important;
    text-decoration: none !important;
    transition: transform 180ms ease !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-primary:active {
    transform: scale(0.985) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .auth-pill,
  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown {
    display: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .menu-pill {
    order: -1 !important;
    margin: 0 10px 0 0 !important;
    border: 1px solid rgba(255, 194, 26, 0.32) !important;
    border-radius: 14px !important;
    background: rgba(255, 194, 26, 0.1) !important;
    color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .brand-pill {
    margin-right: auto !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-account-trigger {
    order: 2 !important;
    display: grid !important;
    flex: 0 0 44px !important;
    width: 44px !important;
    height: 44px !important;
    padding: 0 !important;
    place-items: center !important;
    border: 1px solid rgba(248, 247, 239, 0.14) !important;
    border-radius: 14px !important;
    background: rgba(13, 24, 41, 0.76) !important;
    color: #f8f7ef !important;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-account-trigger > span {
    display: grid !important;
    width: 30px !important;
    height: 30px !important;
    place-items: center !important;
    border-radius: 10px !important;
    background: #ffc21a !important;
    color: #07101d !important;
    font-size: 14px !important;
    font-weight: 850 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-backdrop {
    position: fixed !important;
    inset: 0 !important;
    z-index: 2490 !important;
    display: block !important;
    width: 100vw !important;
    height: 100dvh !important;
    padding: 0 !important;
    border: 0 !important;
    background: rgba(1, 4, 9, 0.68) !important;
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
    transition: opacity 220ms ease, visibility 0s linear 220ms !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-backdrop.open {
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    transition-delay: 0s !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu {
    inset: 0 auto 0 0 !important;
    z-index: 2500 !important;
    width: min(82vw, 348px) !important;
    max-width: calc(100vw - 52px) !important;
    height: 100dvh !important;
    padding: 0 !important;
    overflow: hidden !important;
    border-right: 1px solid rgba(248, 247, 239, 0.1) !important;
    background: #080909 !important;
    box-shadow: 24px 0 64px rgba(0, 0, 0, 0.48) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-head {
    min-height: 88px !important;
    padding: max(16px, env(safe-area-inset-top)) 16px 14px !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 12px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1) !important;
    background: #0b0d0e !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-head > a {
    display: flex !important;
    min-width: 0 !important;
    min-height: 48px !important;
    align-items: center !important;
    gap: 12px !important;
    color: #f8f7ef !important;
    text-decoration: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-avatar,
  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-affiliate-mark {
    display: grid !important;
    flex: 0 0 48px !important;
    width: 48px !important;
    height: 48px !important;
    place-items: center !important;
    border-radius: 15px !important;
    background: rgba(255, 194, 26, 0.14) !important;
    color: #ffc21a !important;
    font-size: 15px !important;
    font-weight: 850 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-identity {
    display: grid !important;
    min-width: 0 !important;
    gap: 4px !important;
    letter-spacing: normal !important;
    text-transform: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-identity strong,
  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-head > a strong {
    overflow: hidden !important;
    color: #f8f7ef !important;
    font-size: 17px !important;
    font-weight: 680 !important;
    line-height: 1.1 !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-identity small {
    overflow: hidden !important;
    color: rgba(248, 247, 239, 0.58) !important;
    font-size: 12px !important;
    font-weight: 500 !important;
    line-height: 1.2 !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-close {
    display: grid !important;
    flex: 0 0 44px !important;
    width: 44px !important;
    height: 44px !important;
    margin-left: auto !important;
    padding: 0 !important;
    place-items: center !important;
    border: 1px solid rgba(248, 247, 239, 0.12) !important;
    border-radius: 14px !important;
    background: rgba(255, 255, 255, 0.05) !important;
    color: #f8f7ef !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-links {
    min-height: 0 !important;
    padding: 0 16px !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    overscroll-behavior: contain !important;
    scrollbar-width: thin !important;
    scrollbar-color: rgba(255, 194, 26, 0.34) transparent !important;
    -webkit-overflow-scrolling: touch !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-group {
    display: grid !important;
    padding: 8px 0 !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.09) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-group:last-child {
    border-bottom: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header
    .mobile-menu-group > :is(a, button) {
    display: grid !important;
    grid-template-columns: 28px minmax(0, 1fr) auto 18px !important;
    width: 100% !important;
    min-height: 52px !important;
    padding: 0 4px !important;
    align-items: center !important;
    gap: 10px !important;
    border: 0 !important;
    border-radius: 12px !important;
    background: transparent !important;
    color: rgba(248, 247, 239, 0.88) !important;
    font: inherit !important;
    font-size: 15px !important;
    font-weight: 560 !important;
    line-height: 1.2 !important;
    text-align: left !important;
    text-decoration: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header
    .mobile-menu-group > :is(a, button):active {
    background: rgba(248, 247, 239, 0.055) !important;
    color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header
    .mobile-menu-group > :is(a, button).active {
    background: rgba(248, 247, 239, 0.055) !important;
    color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-group em {
    display: grid !important;
    min-width: 24px !important;
    height: 24px !important;
    padding: 0 7px !important;
    place-items: center !important;
    border-radius: 999px !important;
    background: #ffc21a !important;
    color: #07101d !important;
    font-size: 11px !important;
    font-style: normal !important;
    font-weight: 850 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-group small {
    color: rgba(248, 247, 239, 0.48) !important;
    font-size: 12px !important;
    font-weight: 620 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-footer {
    padding: 12px 16px max(14px, env(safe-area-inset-bottom)) !important;
    border-top: 1px solid rgba(248, 247, 239, 0.1) !important;
    background: #0b0d0e !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header
    :is(.mobile-menu-primary, .mobile-menu-signout) {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) 20px !important;
    width: 100% !important;
    min-height: 54px !important;
    margin: 0 !important;
    padding: 0 16px !important;
    align-items: center !important;
    border-radius: 14px !important;
    font-size: 15px !important;
    font-weight: 800 !important;
    text-decoration: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-menu-signout {
    grid-template-columns: 28px minmax(0, 1fr) 18px !important;
    gap: 10px !important;
    border: 1px solid rgba(248, 247, 239, 0.12) !important;
    background: rgba(255, 255, 255, 0.05) !important;
    color: #f8f7ef !important;
    text-align: left !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-sticky-link-bar {
    position: static !important;
    order: 1 !important;
    z-index: auto !important;
    display: grid !important;
    grid-template-columns: 16px minmax(0, 1fr) 17px !important;
    flex: 1 1 auto !important;
    width: auto !important;
    min-width: 0 !important;
    min-height: 44px !important;
    margin: 0 8px !important;
    padding: 0 9px !important;
    align-items: center !important;
    gap: 7px !important;
    border: 1px solid rgba(248, 247, 239, 0.13) !important;
    border-radius: 12px !important;
    background: rgba(248, 247, 239, 0.055) !important;
    color: rgba(248, 247, 239, 0.62) !important;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06) !important;
    backdrop-filter: blur(12px) !important;
    text-align: left !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-sticky-link-bar > svg {
    width: 16px !important;
    color: rgba(248, 247, 239, 0.68) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .mobile-sticky-link-bar > span {
    overflow: hidden !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }
}

.mobile-account-trigger,
.mobile-menu-backdrop,
.mobile-menu-close,
.mobile-sticky-link-bar,
.mobile-header-search {
  display: none;
}

@media (max-width: 760px) {
  .mobile-header-search {
    position: fixed;
    inset: 0;
    z-index: 11000;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    width: 100vw;
    height: 100dvh;
    overflow: hidden;
    background: #080909;
    color: #f8f7ef;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(12px);
    transition: opacity 180ms ease, transform 220ms ease, visibility 0s linear 220ms;
  }

  .mobile-header-search.open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
    transition-delay: 0s;
  }

  .mobile-header-search > header {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    min-height: 68px;
    padding: max(12px, env(safe-area-inset-top)) 12px 10px;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1);
    background: rgba(11, 13, 14, 0.96);
    backdrop-filter: blur(18px);
  }

  .mobile-header-search > header > button {
    display: grid;
    width: 44px;
    height: 44px;
    padding: 0;
    place-items: center;
    border: 0;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.06);
    color: #f8f7ef;
    transform: rotate(180deg);
  }

  .mobile-header-search form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 66px;
    min-width: 0;
    height: 48px;
    padding: 3px;
    align-items: center;
    border: 1px solid rgba(248, 247, 239, 0.12);
    border-radius: 15px;
    background: rgba(248, 247, 239, 0.055);
  }

  .mobile-header-search input {
    width: 100%;
    min-width: 0;
    height: 42px;
    padding: 0 12px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #f8f7ef;
    font: inherit;
    font-size: 14px;
  }

  .mobile-header-search input::placeholder {
    color: rgba(248, 247, 239, 0.46);
  }

  .mobile-header-search form button {
    min-width: 62px;
    min-height: 42px;
    padding: 0 12px;
    border: 0;
    border-radius: 12px;
    background: #ffc21a;
    color: #07101d;
    font: inherit;
    font-size: 12px;
    font-weight: 850;
  }

  .mobile-header-search-content {
    min-height: 0;
    padding: 22px 16px max(28px, env(safe-area-inset-bottom));
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .mobile-header-search-content section {
    padding: 0 0 26px;
  }

  .mobile-header-search-content section > div:first-child {
    display: flex;
    min-height: 44px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .mobile-header-search-content section > div:first-child strong {
    font-size: 16px;
    font-weight: 760;
  }

  .mobile-header-search-content section > div:first-child button {
    display: grid;
    width: 44px;
    height: 44px;
    padding: 0;
    place-items: center;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: rgba(248, 247, 239, 0.54);
  }

  .mobile-header-search-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .mobile-header-search-chips :is(a, button) {
    display: inline-flex;
    min-height: 44px;
    max-width: 100%;
    padding: 0 14px;
    align-items: center;
    border: 1px solid rgba(248, 247, 239, 0.12);
    border-radius: 13px;
    background: rgba(248, 247, 239, 0.045);
    color: rgba(248, 247, 239, 0.78);
    font: inherit;
    font-size: 13px;
    font-weight: 620;
    text-decoration: none;
  }
}

/* PRVBUY global shopping bag — shared by every route through HomeHeader. */
html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet {
  position: fixed !important;
  inset: 0 !important;
  z-index: 2500 !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) min(460px, calc(100vw - 24px)) !important;
  pointer-events: auto !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-backdrop {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border: 0 !important;
  background:
    radial-gradient(circle at 78% 12%, rgba(255, 194, 26, 0.09), transparent 28%),
    rgba(2, 3, 3, 0.72) !important;
  backdrop-filter: blur(10px) saturate(0.84) !important;
  -webkit-backdrop-filter: blur(10px) saturate(0.84) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer {
  grid-column: 2 !important;
  position: relative !important;
  z-index: 1 !important;
  display: grid !important;
  grid-template-rows: auto minmax(0, 1fr) auto !important;
  width: min(460px, calc(100vw - 24px)) !important;
  min-width: 0 !important;
  min-height: 100dvh !important;
  overflow: hidden !important;
  border: 0 !important;
  border-right: 0 !important;
  border-radius: 22px 0 0 22px !important;
  background: rgba(12, 13, 12, 0.985) !important;
  color: #fffaf0 !important;
  box-shadow:
    -24px 0 64px rgba(0, 0, 0, 0.34) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-head {
  display: flex !important;
  min-height: 104px !important;
  padding: 24px 28px 20px !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 24px !important;
  border-bottom: 0 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-head span.shopping-bag-kicker {
  display: block !important;
  margin-bottom: 7px !important;
  color: rgba(255, 194, 26, 0.72) !important;
  font-size: 10px !important;
  font-weight: 820 !important;
  line-height: 1 !important;
  letter-spacing: 0.16em !important;
  text-transform: uppercase !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-head h2 {
  margin: 0 !important;
  color: #fffaf0 !important;
  font-size: 27px !important;
  font-weight: 720 !important;
  line-height: 1 !important;
  letter-spacing: -0.04em !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-head p {
  margin: 8px 0 0 !important;
  color: rgba(255, 250, 240, 0.5) !important;
  font-size: 13px !important;
  font-weight: 520 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-close {
  display: grid !important;
  flex: 0 0 42px !important;
  width: 42px !important;
  height: 42px !important;
  padding: 0 !important;
  place-items: center !important;
  border: 0 !important;
  border-radius: 12px !important;
  background: transparent !important;
  color: rgba(255, 250, 240, 0.72) !important;
  transition: background 180ms ease, color 180ms ease, transform 180ms ease !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-close:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  color: #ffc21a !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-close:active {
  transform: scale(0.96) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-body {
  min-height: 0 !important;
  padding: 18px 28px !important;
  overflow-y: auto !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-row {
  display: grid !important;
  grid-template-columns: 88px minmax(0, 1fr) auto !important;
  gap: 16px !important;
  min-height: 0 !important;
  padding: 12px 0 20px !important;
  align-items: start !important;
  border: 0 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-empty {
  display: grid !important;
  gap: 8px !important;
  min-height: 140px !important;
  align-content: center !important;
  padding: 24px 0 32px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-empty strong {
  color: #fffaf0 !important;
  font-size: 17px !important;
  font-weight: 800 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-empty span {
  color: rgba(255, 250, 240, 0.62) !important;
  font-size: 13px !important;
  line-height: 1.5 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-retry {
  width: fit-content !important;
  min-height: 42px !important;
  margin-top: 8px !important;
  padding: 0 18px !important;
  border: 1px solid rgba(255, 194, 26, 0.42) !important;
  border-radius: 12px !important;
  background: rgba(255, 194, 26, 0.1) !important;
  color: #ffc21a !important;
  font-size: 13px !important;
  font-weight: 720 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-operation-error {
  margin: 14px 0 0 !important;
  color: #ffb4a8 !important;
  font-size: 12px !important;
  line-height: 1.45 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-image {
  width: 88px !important;
  height: 88px !important;
  overflow: hidden !important;
  border: 0 !important;
  border-radius: 13px !important;
  background: #ece9e1 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-image img {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-main {
  display: grid !important;
  min-width: 0 !important;
  gap: 5px !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-main > strong {
  color: #fffaf0 !important;
  font-size: 16px !important;
  font-weight: 720 !important;
  line-height: 1.24 !important;
  letter-spacing: -0.02em !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-main > span {
  color: rgba(255, 250, 240, 0.5) !important;
  font-size: 12px !important;
  font-weight: 520 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-main > small {
  display: block !important;
  overflow: hidden !important;
  color: rgba(255, 194, 26, 0.68) !important;
  font-size: 10px !important;
  font-weight: 620 !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-quantity {
  display: grid !important;
  grid-template-columns: 30px 34px 30px !important;
  width: 94px !important;
  height: 30px !important;
  margin-top: 7px !important;
  overflow: hidden !important;
  border: 0 !important;
  border-radius: 9px !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-quantity :is(button, span) {
  display: grid !important;
  min-width: 0 !important;
  padding: 0 !important;
  place-items: center !important;
  border: 0 !important;
  background: transparent !important;
  color: rgba(255, 250, 240, 0.82) !important;
  font-size: 14px !important;
  font-weight: 680 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-quantity button:hover {
  background: rgba(255, 194, 26, 0.12) !important;
  color: #ffc21a !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet :is(.shopping-bag-quantity button, .shopping-bag-product-side button):disabled {
  cursor: not-allowed !important;
  opacity: 0.38 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-side {
  display: grid !important;
  height: auto !important;
  min-height: 88px !important;
  align-content: space-between !important;
  justify-items: end !important;
  gap: 16px !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-side button {
  display: grid !important;
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  place-items: center !important;
  border: 0 !important;
  border-radius: 9px !important;
  background: transparent !important;
  color: rgba(255, 250, 240, 0.4) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-side button:hover {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #fffaf0 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-side strong {
  display: block !important;
  min-width: 0 !important;
  height: auto !important;
  padding: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  color: #ffc21a !important;
  font-size: 16px !important;
  font-weight: 780 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-summary {
  display: grid !important;
  gap: 12px !important;
  padding: 20px 28px 24px !important;
  border-top: 1px solid rgba(255, 255, 255, 0.055) !important;
  background: rgba(7, 8, 7, 0.72) !important;
  backdrop-filter: blur(18px) !important;
  -webkit-backdrop-filter: blur(18px) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-summary > div {
  display: flex !important;
  align-items: baseline !important;
  justify-content: space-between !important;
  gap: 18px !important;
  color: rgba(255, 250, 240, 0.56) !important;
  font-size: 13px !important;
  font-weight: 560 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-summary > div strong {
  color: #fffaf0 !important;
  font-size: 24px !important;
  font-weight: 780 !important;
  letter-spacing: -0.035em !important;
  text-align: right !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-summary-note {
  margin: -4px 0 2px !important;
  color: rgba(255, 250, 240, 0.38) !important;
  font-size: 11px !important;
  line-height: 1.35 !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet :is(.shopping-bag-view, .shopping-bag-checkout) {
  display: inline-flex !important;
  min-height: 48px !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 14px !important;
  font-size: 15px !important;
  font-weight: 720 !important;
  transition: transform 180ms ease, background 180ms ease, border-color 180ms ease !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-view {
  min-height: 38px !important;
  border: 0 !important;
  background: transparent !important;
  color: rgba(255, 250, 240, 0.76) !important;
  text-decoration: underline !important;
  text-decoration-color: rgba(255, 255, 255, 0.2) !important;
  text-underline-offset: 5px !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-checkout {
  border: 1px solid #ffc21a !important;
  background: #ffc21a !important;
  color: #12120f !important;
  box-shadow: 0 8px 22px rgba(255, 194, 26, 0.12) !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-checkout:disabled {
  cursor: not-allowed !important;
  border-color: rgba(255, 250, 240, 0.12) !important;
  background: rgba(255, 250, 240, 0.06) !important;
  color: rgba(255, 250, 240, 0.34) !important;
  box-shadow: none !important;
}

html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet :is(.shopping-bag-view, .shopping-bag-checkout):active {
  transform: scale(0.985) !important;
}

@media (max-width: 720px) {
  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet {
    grid-template-columns: 1fr !important;
  }

  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer {
    grid-column: 1 !important;
    width: 100% !important;
    border: 0 !important;
    border-radius: 24px 24px 0 0 !important;
  }

  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-head {
    min-height: 104px !important;
    padding: 22px 20px 20px !important;
  }

  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-drawer-body {
    padding: 18px 16px !important;
  }

  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-row {
    grid-template-columns: 76px minmax(0, 1fr) auto !important;
    gap: 12px !important;
    padding: 12px !important;
  }

  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-product-image {
    width: 76px !important;
    height: 76px !important;
  }

  html body .shopping-bag-sheet.prvbuy-shopping-bag-sheet .shopping-bag-summary {
    padding: 18px 16px max(20px, env(safe-area-inset-bottom)) !important;
  }
}

.prvbuy-floating-tools {
  position: fixed;
  inset: auto 0 170px auto;
  z-index: 1180;
  display: grid;
  gap: 4px;
  padding: 8px 7px 8px 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 0;
  border-radius: 18px 0 0 18px;
  background: rgba(14, 15, 14, 0.78);
  box-shadow: -12px 18px 40px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(18px) saturate(118%);
  -webkit-backdrop-filter: blur(18px) saturate(118%);
}

.prvbuy-floating-tool {
  position: relative;
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border: 0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.055);
  color: rgba(255, 250, 240, 0.82);
  text-decoration: none;
  transition: background 180ms ease, color 180ms ease, transform 180ms ease;
}

.prvbuy-floating-tool:hover,
.prvbuy-floating-tool:focus-visible {
  z-index: 2;
  background: #ffc21a;
  color: #11120f;
  outline: none;
  transform: translateX(-3px);
}

.prvbuy-floating-tool-icon {
  display: grid;
  width: 25px;
  height: 25px;
  place-items: center;
}

.prvbuy-floating-tool-icon img {
  width: 25px;
  height: 25px;
  object-fit: contain;
  filter: grayscale(1) brightness(2.3);
  transition: filter 180ms ease;
}

.prvbuy-floating-tool:hover .prvbuy-floating-tool-icon img,
.prvbuy-floating-tool:focus-visible .prvbuy-floating-tool-icon img {
  filter: brightness(0.18);
}

.prvbuy-floating-tool-label {
  position: absolute;
  top: 50%;
  right: calc(100% + 12px);
  width: max-content;
  padding: 9px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
  background: rgba(15, 16, 15, 0.94);
  color: #fffaf0;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
  font-size: 12px;
  font-weight: 680;
  letter-spacing: -0.01em;
  opacity: 0;
  pointer-events: none;
  transform: translate(8px, -50%);
  transition: opacity 160ms ease, transform 180ms ease;
}

.prvbuy-floating-tool-label::after {
  position: absolute;
  top: 50%;
  left: 100%;
  width: 8px;
  height: 8px;
  background: rgba(15, 16, 15, 0.94);
  content: "";
  transform: translate(-4px, -50%) rotate(45deg);
}

.prvbuy-floating-tool:hover .prvbuy-floating-tool-label,
.prvbuy-floating-tool:focus-visible .prvbuy-floating-tool-label {
  opacity: 1;
  transform: translate(0, -50%);
}

.prvbuy-guide-launcher {
  position: fixed;
  inset: auto auto 28px 24px;
  z-index: 1170;
  display: grid;
  grid-template-columns: 58px auto;
  width: 202px;
  min-height: 64px;
  padding: 5px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  background: rgba(14, 15, 14, 0.84);
  color: #fffaf0;
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.2);
  text-align: left;
  backdrop-filter: blur(18px) saturate(118%);
  -webkit-backdrop-filter: blur(18px) saturate(118%);
  transition: transform 200ms ease, border-color 200ms ease, background 200ms ease;
}

.prvbuy-guide-launcher:hover,
.prvbuy-guide-launcher:focus-visible {
  border-color: rgba(255, 194, 26, 0.5);
  background: rgba(18, 19, 17, 0.94);
  outline: none;
  transform: translateY(-3px);
}

.prvbuy-guide-launcher-visual {
  position: relative;
  display: grid;
  width: 54px;
  min-height: 54px;
  place-items: center;
  overflow: hidden;
  border-radius: 14px;
  background: #ffc21a;
  color: #11120f;
}

.prvbuy-guide-launcher-visual::before {
  position: absolute;
  inset: 7px;
  border: 1px solid rgba(17, 18, 15, 0.17);
  border-radius: 10px;
  content: "";
}

.prvbuy-guide-launcher-copy {
  display: flex;
  min-width: 0;
  padding: 6px 10px;
  flex-direction: column;
  justify-content: center;
}

.prvbuy-guide-launcher-copy strong {
  color: #fffaf0;
  font-size: 14px;
  font-weight: 720;
  letter-spacing: -0.025em;
  line-height: 1.15;
}

.prvbuy-guide-launcher-copy span {
  margin-top: 4px;
  color: rgba(255, 250, 240, 0.48);
  font-size: 10px;
  font-weight: 580;
}

@media (min-width: 1024px) {
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header .prvbuy-guide-launcher {
    left: max(346px, calc((100vw - 1760px) / 2 + 314px));
    grid-template-columns: 48px;
    width: 54px;
    min-height: 54px;
    padding: 3px;
    border-radius: 16px;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header .prvbuy-guide-launcher-visual {
    width: 46px;
    min-height: 46px;
    border-radius: 13px;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header .prvbuy-guide-launcher-copy {
    display: none;
  }
}

.prvbuy-guide-modal {
  position: fixed;
  inset: 0;
  z-index: 2800;
  display: grid;
  padding: 24px;
  place-items: center;
}

.prvbuy-guide-backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(5, 6, 5, 0.76);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.prvbuy-guide-dialog {
  position: relative;
  width: min(900px, calc(100vw - 48px));
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 26px;
  background: #11120f;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.48);
}

.prvbuy-guide-dialog-head {
  display: flex;
  min-height: 74px;
  padding: 16px 18px 14px 24px;
  align-items: center;
  justify-content: space-between;
}

.prvbuy-guide-dialog-head span {
  display: block;
  margin-bottom: 3px;
  color: #ffc21a;
  font-size: 10px;
  font-weight: 760;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.prvbuy-guide-dialog-head h2 {
  margin: 0;
  color: #fffaf0;
  font-size: 22px;
  font-weight: 740;
  letter-spacing: -0.04em;
}

.prvbuy-guide-close {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  color: #fffaf0;
  transition: background 180ms ease, color 180ms ease, transform 180ms ease;
}

.prvbuy-guide-close:hover,
.prvbuy-guide-close:focus-visible {
  background: #ffc21a;
  color: #11120f;
  outline: none;
  transform: rotate(5deg);
}

.prvbuy-guide-media {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #090a09;
}

.prvbuy-guide-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.72;
}

.prvbuy-guide-media::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0.42));
  content: "";
}

.prvbuy-guide-play {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: grid;
  width: 76px;
  height: 76px;
  place-items: center;
  border-radius: 50%;
  background: #ffc21a;
  color: #11120f;
  box-shadow: 0 14px 40px rgba(255, 194, 26, 0.24);
  transform: translate(-50%, -50%);
}

.prvbuy-guide-dialog-foot {
  display: flex;
  min-height: 72px;
  padding: 14px 18px 16px 24px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.prvbuy-guide-dialog-foot p {
  margin: 0;
  color: rgba(255, 250, 240, 0.52);
  font-size: 12px;
  line-height: 1.45;
}

.prvbuy-guide-youtube {
  display: inline-flex;
  min-height: 42px;
  padding: 0 15px;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background: #fffaf0;
  color: #11120f;
  font-size: 12px;
  font-weight: 720;
  text-decoration: none;
}

@media (max-width: 720px) {
  .prvbuy-floating-tools {
    bottom: calc(92px + env(safe-area-inset-bottom));
    padding: 5px;
    gap: 2px;
    border-radius: 15px 0 0 15px;
  }

  .prvbuy-floating-tool {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  .prvbuy-floating-tool-label {
    display: none;
  }

  .prvbuy-guide-launcher {
    bottom: calc(18px + env(safe-area-inset-bottom));
    left: 14px;
    grid-template-columns: 48px;
    width: 54px;
    min-height: 54px;
    padding: 3px;
    border-radius: 16px;
  }

  .prvbuy-guide-launcher-visual {
    width: 46px;
    min-height: 46px;
    border-radius: 13px;
  }

  .prvbuy-guide-launcher-copy {
    display: none;
  }

  .prvbuy-guide-modal {
    padding: 12px;
  }

  .prvbuy-guide-dialog {
    width: calc(100vw - 24px);
    border-radius: 20px;
  }

  .prvbuy-guide-dialog-head {
    min-height: 68px;
    padding: 13px 13px 12px 18px;
  }

  .prvbuy-guide-dialog-head h2 {
    font-size: 18px;
  }

  .prvbuy-guide-dialog-foot {
    padding: 15px 18px 18px;
    align-items: flex-start;
    flex-direction: column;
  }

  .prvbuy-guide-youtube {
    width: 100%;
    justify-content: center;
  }
}

@media (min-width: 761px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header :is(.locale-popover, .account-dropdown) {
    border: 1px solid rgba(248, 247, 239, 0.16) !important;
    border-radius: 16px !important;
    background: rgba(10, 13, 12, 0.97) !important;
    box-shadow: 0 8px 10px rgba(0, 0, 0, 0.34) !important;
    backdrop-filter: blur(18px) !important;
    -webkit-backdrop-filter: blur(18px) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover {
    width: min(468px, calc(100vw - 40px)) !important;
    min-width: 420px !important;
    padding: 16px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-arrow {
    right: 20px !important;
    width: 14px !important;
    height: 14px !important;
    border-color: rgba(248, 247, 239, 0.16) !important;
    background: #0a0d0c !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-head {
    display: flex !important;
    min-height: 48px !important;
    padding: 0 2px 14px !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
    gap: 16px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-head > div,
  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-identity {
    display: grid !important;
    min-width: 0 !important;
    gap: 4px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-head strong {
    color: #f8f7ef !important;
    font-size: 15px !important;
    font-weight: 760 !important;
    letter-spacing: -0.015em !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-head > div > span {
    color: rgba(248, 247, 239, 0.48) !important;
    font-size: 11px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-current {
    padding-top: 2px !important;
    color: #ffc21a !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    letter-spacing: 0.06em !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-columns {
    display: grid !important;
    grid-template-columns: 1fr 0.88fr !important;
    gap: 20px !important;
    padding-top: 14px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-columns .locale-section {
    min-width: 0 !important;
    margin: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-popover-columns .locale-section + .locale-section {
    padding-left: 20px !important;
    border-left: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-heading,
  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-label {
    margin: 0 0 8px !important;
    color: rgba(248, 247, 239, 0.42) !important;
    font-size: 10px !important;
    font-weight: 800 !important;
    line-height: 1 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 4px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option {
    position: relative !important;
    height: 34px !important;
    min-height: 34px !important;
    padding: 0 9px 0 18px !important;
    justify-content: flex-start !important;
    border: 0 !important;
    border-radius: 7px !important;
    background: transparent !important;
    color: rgba(248, 247, 239, 0.68) !important;
    font-size: 12px !important;
    font-weight: 650 !important;
    text-align: left !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option::before {
    position: absolute !important;
    left: 7px !important;
    width: 4px !important;
    height: 4px !important;
    border-radius: 50% !important;
    background: rgba(248, 247, 239, 0.22) !important;
    content: "" !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option:hover {
    background: rgba(248, 247, 239, 0.07) !important;
    color: #f8f7ef !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option.active {
    background: rgba(255, 194, 26, 0.1) !important;
    color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option.active::before {
    background: #ffc21a !important;
    box-shadow: 0 0 0 3px rgba(255, 194, 26, 0.12) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .locale-option.unavailable:disabled {
    cursor: not-allowed !important;
    opacity: 0.58 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown {
    width: 440px !important;
    min-width: 440px !important;
    padding: 16px !important;
    transform: translateX(-62%) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-head {
    display: grid !important;
    grid-template-columns: 42px minmax(0, 1fr) auto !important;
    min-height: 58px !important;
    padding: 0 2px 14px !important;
    align-items: center !important;
    gap: 11px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-avatar {
    display: grid !important;
    width: 42px !important;
    height: 42px !important;
    place-items: center !important;
    border-radius: 11px !important;
    background: #ffc21a !important;
    color: #090b0a !important;
    font-size: 16px !important;
    font-weight: 850 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-identity strong {
    overflow: hidden !important;
    color: #f8f7ef !important;
    font-size: 14px !important;
    font-weight: 760 !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-identity span {
    overflow: hidden !important;
    color: rgba(248, 247, 239, 0.46) !important;
    font-size: 11px !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-head > a {
    color: #ffc21a !important;
    font-size: 11px !important;
    font-weight: 750 !important;
    text-decoration: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-affiliate-card {
    display: grid !important;
    grid-template-columns: 34px minmax(0, 1fr) auto !important;
    min-height: 52px !important;
    margin: 12px 0 !important;
    padding: 7px 10px !important;
    align-items: center !important;
    gap: 10px !important;
    border: 1px solid rgba(255, 194, 26, 0.18) !important;
    border-radius: 9px !important;
    background: rgba(255, 194, 26, 0.055) !important;
    text-decoration: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-affiliate-card > span:first-child {
    display: grid !important;
    width: 34px !important;
    height: 34px !important;
    place-items: center !important;
    border-radius: 8px !important;
    background: #ffc21a !important;
    color: #090b0a !important;
    font-size: 10px !important;
    font-weight: 900 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-affiliate-card > span:nth-child(2) {
    display: grid !important;
    gap: 3px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-affiliate-card strong {
    color: #f8f7ef !important;
    font-size: 12px !important;
    font-weight: 730 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-affiliate-card small {
    color: rgba(248, 247, 239, 0.42) !important;
    font-size: 10px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-affiliate-card b {
    color: #ffc21a !important;
    font-size: 13px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-sections {
    display: grid !important;
    grid-template-columns: 0.82fr 1.5fr !important;
    gap: 16px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-section-wide {
    padding-left: 16px !important;
    border-left: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 3px 5px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links-compact {
    grid-template-columns: 1fr !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links > a {
    position: relative !important;
    height: 32px !important;
    min-height: 32px !important;
    padding: 0 7px 0 17px !important;
    border-radius: 7px !important;
    color: rgba(248, 247, 239, 0.65) !important;
    font-size: 11px !important;
    font-weight: 650 !important;
    white-space: nowrap !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links > a::before {
    position: absolute !important;
    left: 7px !important;
    width: 3px !important;
    height: 3px !important;
    border-radius: 50% !important;
    background: rgba(248, 247, 239, 0.26) !important;
    content: "" !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links > a:hover {
    background: rgba(248, 247, 239, 0.07) !important;
    color: #f8f7ef !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-links > a:hover::before {
    background: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-foot {
    display: flex !important;
    min-height: 38px !important;
    margin-top: 12px !important;
    padding: 10px 2px 0 !important;
    align-items: center !important;
    justify-content: space-between !important;
    border-top: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-foot > span {
    color: rgba(248, 247, 239, 0.32) !important;
    font-size: 10px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-menu-signout {
    width: auto !important;
    height: 28px !important;
    min-height: 28px !important;
    padding: 0 9px !important;
    border-radius: 6px !important;
    color: rgba(248, 247, 239, 0.58) !important;
    font-size: 10px !important;
    font-weight: 700 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown {
    top: 48px !important;
    right: -76px !important;
    left: auto !important;
    display: block !important;
    width: 320px !important;
    min-width: 320px !important;
    max-height: calc(100dvh - 76px) !important;
    padding: 18px 16px 14px !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    transform: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown::after {
    left: calc(100% - 100px) !important;
    right: auto !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-identity {
    display: grid !important;
    min-height: 58px !important;
    padding: 2px 10px 16px !important;
    align-content: center !important;
    gap: 5px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.11) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-identity strong {
    color: #f8f7ef !important;
    font-size: 15px !important;
    font-weight: 740 !important;
    letter-spacing: -0.015em !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-identity span {
    color: rgba(248, 247, 239, 0.46) !important;
    font-size: 12px !important;
    font-weight: 540 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-links {
    display: block !important;
    padding: 8px 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-links > a {
    position: relative !important;
    display: flex !important;
    width: 100% !important;
    height: 44px !important;
    min-height: 44px !important;
    padding: 0 28px 0 10px !important;
    align-items: center !important;
    border: 0 !important;
    border-radius: 7px !important;
    background: transparent !important;
    color: rgba(248, 247, 239, 0.72) !important;
    font-size: 13px !important;
    font-weight: 620 !important;
    line-height: 1 !important;
    text-decoration: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-links > a::before {
    content: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-links > a::after {
    position: absolute !important;
    right: 10px !important;
    width: 5px !important;
    height: 5px !important;
    border-top: 1px solid rgba(248, 247, 239, 0.3) !important;
    border-right: 1px solid rgba(248, 247, 239, 0.3) !important;
    content: "" !important;
    transform: rotate(45deg) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-links > a:hover {
    background: rgba(248, 247, 239, 0.065) !important;
    color: #f8f7ef !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-links > a:hover::after {
    border-color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-signout {
    display: flex !important;
    width: 100% !important;
    height: 44px !important;
    min-height: 44px !important;
    padding: 0 10px !important;
    align-items: center !important;
    border-top: 1px solid rgba(248, 247, 239, 0.11) !important;
    border-radius: 0 !important;
    background: transparent !important;
    color: rgba(248, 247, 239, 0.54) !important;
    font-size: 12px !important;
    font-weight: 650 !important;
    text-align: left !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .account-dropdown > .account-menu-signout:hover {
    color: #ffc21a !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header :is(.locale-option, .account-dropdown a, .account-dropdown button):focus-visible {
    outline: 2px solid #ffc21a !important;
    outline-offset: 2px !important;
  }

  /* User Center nests the shared Header inside the portal shell. Restore the
     Homepage locale panel instead of the legacy fixed, oversized portal skin. */
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover {
    position: absolute !important;
    top: 48px !important;
    right: 0 !important;
    left: auto !important;
    z-index: 2600 !important;
    width: min(468px, calc(100vw - 40px)) !important;
    min-width: 420px !important;
    max-width: calc(100vw - 40px) !important;
    height: auto !important;
    max-height: calc(100dvh - 76px) !important;
    padding: 16px !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    border: 1px solid rgba(248, 247, 239, 0.16) !important;
    border-radius: 16px !important;
    background: rgba(10, 13, 12, 0.97) !important;
    box-shadow: 0 8px 10px rgba(0, 0, 0, 0.34) !important;
    color: #f8f7ef !important;
    transform: none !important;
    -webkit-backdrop-filter: blur(18px) !important;
    backdrop-filter: blur(18px) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover::before {
    display: none !important;
    content: none !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-arrow.locale-popover-arrow {
    top: -7px !important;
    right: 20px !important;
    left: auto !important;
    width: 14px !important;
    height: 14px !important;
    border: 1px solid rgba(248, 247, 239, 0.16) !important;
    border-right: 0 !important;
    border-bottom: 0 !important;
    background: #0a0d0c !important;
    box-shadow: none !important;
    transform: rotate(45deg) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-head.locale-popover-head {
    display: flex !important;
    min-height: 48px !important;
    padding: 0 2px 14px !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
    gap: 16px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-head.locale-popover-head > div {
    display: grid !important;
    min-width: 0 !important;
    gap: 4px !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-head.locale-popover-head strong {
    color: #f8f7ef !important;
    font-size: 15px !important;
    font-weight: 760 !important;
    line-height: normal !important;
    letter-spacing: -0.015em !important;
    white-space: nowrap !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-head.locale-popover-head > div > span {
    color: rgba(248, 247, 239, 0.48) !important;
    font-size: 11px !important;
    font-weight: 500 !important;
    line-height: normal !important;
    white-space: nowrap !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-head.locale-popover-head > .locale-current.locale-current {
    padding-top: 2px !important;
    color: #ffc21a !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    line-height: normal !important;
    letter-spacing: 0.06em !important;
    white-space: nowrap !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-columns.locale-popover-columns {
    display: grid !important;
    grid-template-columns: 1fr 0.88fr !important;
    gap: 20px !important;
    padding-top: 14px !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-columns.locale-popover-columns > .locale-section.locale-section {
    min-width: 0 !important;
    margin: 0 !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover
    > .locale-popover-columns.locale-popover-columns > .locale-section.locale-section + .locale-section.locale-section {
    padding-left: 20px !important;
    border-left: 1px solid rgba(248, 247, 239, 0.1) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-heading.locale-heading {
    margin: 0 0 8px !important;
    color: rgba(248, 247, 239, 0.42) !important;
    font-size: 10px !important;
    font-weight: 800 !important;
    line-height: 1 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-grid.locale-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 4px !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-option.locale-option {
    position: relative !important;
    display: flex !important;
    width: 100% !important;
    height: 34px !important;
    min-height: 34px !important;
    padding: 0 9px 0 18px !important;
    align-items: center !important;
    justify-content: flex-start !important;
    border: 0 !important;
    border-radius: 7px !important;
    background: transparent !important;
    box-shadow: none !important;
    color: rgba(248, 247, 239, 0.68) !important;
    font-size: 12px !important;
    font-weight: 650 !important;
    line-height: 1 !important;
    text-align: left !important;
    transform: none !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-option.locale-option::before {
    position: absolute !important;
    left: 7px !important;
    width: 4px !important;
    height: 4px !important;
    border: 0 !important;
    border-radius: 50% !important;
    background: rgba(248, 247, 239, 0.22) !important;
    box-shadow: none !important;
    content: "" !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-option.locale-option:hover {
    background: rgba(248, 247, 239, 0.07) !important;
    color: #f8f7ef !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-option.locale-option.active {
    background: rgba(255, 194, 26, 0.1) !important;
    color: #ffc21a !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-option.locale-option.active::before {
    background: #ffc21a !important;
    box-shadow: 0 0 0 3px rgba(255, 194, 26, 0.12) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .locale-picker.locale-picker > .locale-popover.locale-popover .locale-option.locale-option:focus-visible {
    outline: 2px solid #ffc21a !important;
    outline-offset: 2px !important;
  }

  /* User Center renders the shared Header inside a portal wrapper. Keep its
     desktop account menu on the same final compact treatment as Homepage. */
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown {
    position: absolute !important;
    top: 48px !important;
    right: -76px !important;
    left: auto !important;
    z-index: 2600 !important;
    width: 320px !important;
    min-width: 320px !important;
    max-width: 320px !important;
    height: auto !important;
    max-height: calc(100dvh - 76px) !important;
    padding: 18px 16px 14px !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    border: 1px solid rgba(248, 247, 239, 0.16) !important;
    border-radius: 16px !important;
    background: rgba(10, 13, 12, 0.97) !important;
    box-shadow: 0 8px 10px rgba(0, 0, 0, 0.34) !important;
    color: #f8f7ef !important;
    opacity: 1 !important;
    visibility: visible !important;
    transform: none !important;
    -webkit-backdrop-filter: blur(18px) !important;
    backdrop-filter: blur(18px) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown::before {
    display: none !important;
    content: none !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown::after {
    position: absolute !important;
    top: -11px !important;
    right: auto !important;
    left: calc(100% - 100px) !important;
    width: 24px !important;
    height: 24px !important;
    border-top: 1px solid rgba(255, 255, 255, 0.11) !important;
    border-right: 0 !important;
    border-bottom: 0 !important;
    border-left: 1px solid rgba(255, 255, 255, 0.11) !important;
    background: rgb(17, 27, 44) !important;
    box-shadow: none !important;
    content: "" !important;
    transform: translateX(-50%) rotate(45deg) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-identity.account-menu-identity {
    display: grid !important;
    min-width: 0 !important;
    min-height: 58px !important;
    margin-top: 0 !important;
    padding: 2px 10px 16px !important;
    align-content: center !important;
    gap: 5px !important;
    border-bottom: 1px solid rgba(248, 247, 239, 0.11) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-identity.account-menu-identity strong {
    color: #f8f7ef !important;
    font-size: 15px !important;
    font-weight: 740 !important;
    line-height: normal !important;
    letter-spacing: -0.015em !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-identity.account-menu-identity span {
    color: rgba(248, 247, 239, 0.46) !important;
    font-size: 12px !important;
    font-weight: 540 !important;
    line-height: normal !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-links.account-menu-links {
    display: block !important;
    padding: 8px 0 !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-links.account-menu-links > a {
    position: relative !important;
    display: flex !important;
    width: 100% !important;
    height: 44px !important;
    min-height: 44px !important;
    padding: 0 28px 0 10px !important;
    align-items: center !important;
    border: 0 !important;
    border-radius: 7px !important;
    background: transparent !important;
    color: rgba(248, 247, 239, 0.72) !important;
    font-size: 13px !important;
    font-weight: 620 !important;
    line-height: 1 !important;
    text-decoration: none !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-links.account-menu-links > a::before {
    content: none !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-links.account-menu-links > a::after {
    position: absolute !important;
    right: 10px !important;
    width: 5px !important;
    height: 5px !important;
    border-top: 1px solid rgba(248, 247, 239, 0.3) !important;
    border-right: 1px solid rgba(248, 247, 239, 0.3) !important;
    content: "" !important;
    transform: rotate(45deg) !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-links.account-menu-links > a:hover {
    background: rgba(248, 247, 239, 0.065) !important;
    color: #f8f7ef !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-links.account-menu-links > a:hover::after {
    border-color: #ffc21a !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-signout.account-menu-signout {
    display: flex !important;
    width: 100% !important;
    height: 44px !important;
    min-height: 44px !important;
    padding: 0 10px !important;
    align-items: center !important;
    border: 0 !important;
    border-top: 1px solid rgba(248, 247, 239, 0.11) !important;
    border-radius: 0 !important;
    background: transparent !important;
    color: rgba(248, 247, 239, 0.54) !important;
    font-size: 12px !important;
    font-weight: 650 !important;
    line-height: 1 !important;
    text-align: left !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    > .account-menu-signout.account-menu-signout:hover {
    background: transparent !important;
    color: #ffc21a !important;
  }

  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header
    .auth-pill.auth-pill-signed-in > .account-dropdown.account-dropdown
    :is(a, button):focus-visible {
    outline: 2px solid #ffc21a !important;
    outline-offset: 2px !important;
  }
}

/* Shared desktop mount contract. Homepage mounts the Header directly under
   body, while PortalShell mounts the same Header inside .portal-desktop-header.
   Keep both surfaces on one geometry source of truth so route-level legacy
   portal CSS cannot stretch the four public links into uneven columns. */
@media (min-width: 1400px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav {
    position: relative !important;
    display: flex !important;
    gap: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill {
    flex: 0 0 240px !important;
    width: 240px !important;
    min-width: 240px !important;
    margin: 0 !important;
    order: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry {
    flex: 0 0 260px !important;
    width: 260px !important;
    min-width: 260px !important;
    margin: 0 0 0 -20px !important;
    order: 1 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill {
    position: absolute !important;
    left: 50% !important;
    display: flex !important;
    flex: 0 0 auto !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    align-items: center !important;
    justify-content: center !important;
    gap: clamp(42px, calc(25.5px + 1.15vw), 48px) !important;
    transform: translateX(-50%) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill > a,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill > a {
    flex: 0 0 auto !important;
    width: auto !important;
    min-width: 0 !important;
    padding-inline: 2px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools {
    display: flex !important;
    margin-left: auto !important;
    padding-right: 42px !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 12px !important;
    order: 3 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools
    > :is(.locale-picker, .auth-pill, .shopping-bag-trigger),
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools
    > :is(.locale-picker, .auth-pill, .shopping-bag-trigger) {
    margin: 0 !important;
  }
}

@media (min-width: 1100px) and (max-width: 1399px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav {
    position: relative !important;
    display: flex !important;
    gap: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .brand-pill {
    flex: 0 0 160px !important;
    width: 160px !important;
    min-width: 160px !important;
    order: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-link-entry {
    flex: 0 0 156px !important;
    width: 156px !important;
    min-width: 156px !important;
    order: 1 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill {
    position: absolute !important;
    left: 50% !important;
    display: flex !important;
    flex: 0 0 auto !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    margin: 0 !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 26px !important;
    transform: translateX(-50%) !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill > a,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .nav-pill > a {
    flex: 0 0 auto !important;
    width: auto !important;
    min-width: 0 !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools,
  html:has(.portal-blue-shell) body:has(.portal-blue-shell)
    .portal-desktop-header > #prvbuy-global-header.cd-header.home-shared-header > nav.cd-nav.cd-nav > .header-tools {
    display: flex !important;
    margin-left: auto !important;
    padding-right: 42px !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 12px !important;
    order: 3 !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .prvbuy-floating-tool,
  .prvbuy-floating-tool-label,
  .prvbuy-guide-launcher,
  .prvbuy-guide-close {
    transition: none;
  }
}

@media (max-width: 760px) {
  html body > #prvbuy-global-header.cd-header.home-shared-header .brand-pill.brand-pill {
    width: 44px !important;
    min-width: 44px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header .cart-pill.cart-pill {
    width: 44px !important;
    min-width: 44px !important;
    height: 44px !important;
    min-height: 44px !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header nav.cd-nav.cd-nav
    > :is(.auth-pill.auth-pill, .mobile-account-trigger.mobile-account-trigger) {
    display: none !important;
  }

  html body > #prvbuy-global-header.cd-header.home-shared-header
    .account-avatar-button.account-avatar-button {
    width: 44px !important;
    min-width: 44px !important;
    max-width: 44px !important;
    height: 44px !important;
    min-height: 44px !important;
    max-height: 44px !important;
  }
}
`;

type HomeHeaderProps = {
  initialSignedIn?: boolean;
  initialSession?: AuthSession | null;
  linkPlaceholder?: string;
  mobileLayers?: boolean;
};

export function HomeHeader({
  initialSession = null,
  linkPlaceholder = "Enter the link or keyword",
  mobileLayers = true,
}: HomeHeaderProps) {
  const pathname = usePathname();
  const activeNav = Math.max(0, navItems.findIndex((item) => item.href === pathname));
  const [menuOpen, setMenuOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [authSession, setAuthSession] = useState<AuthSession | null>(initialSession);
  const { isSigningOut, signOut } = useCustomerLogout();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [shoppingBagOpen, setShoppingBagOpen] = useState(false);
  const [shoppingBagItems, setShoppingBagItems] = useState<HeaderCartItem[]>([]);
  const [shoppingBagStatus, setShoppingBagStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [shoppingBagError, setShoppingBagError] = useState("");
  const [shoppingBagBusyIds, setShoppingBagBusyIds] = useState<string[]>([]);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchMode, setMobileSearchMode] = useState<"product" | "image">("product");
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [headerLink, setHeaderLink] = useState("");
  const localeValue = useSyncExternalStore(subscribeLocale, localeSnapshot, () => `${supportedLocale.language}|${supportedLocale.currency}`);
  const [localeLanguage, localeCurrency] = localeValue.split("|");

  function selectLanguage(language: "EN" | "CN") {
    const next = { language, currency: localeCurrency || supportedLocale.currency };
    saveLocalePreferences(next);
    setRegionOpen(false);
    const url = new URL(window.location.href);
    if (url.pathname.startsWith("/product/")) url.searchParams.set("language", language);
    window.location.assign(url.toString());
  }
  const regionCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accountCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accountHoverRegionRef = useRef<HTMLDivElement | null>(null);
  const accountDropdownRef = useRef<HTMLDivElement | null>(null);
  const accountBridgeRef = useRef<HTMLDivElement | null>(null);
  const shoppingBagButtonRef = useRef<HTMLButtonElement | null>(null);
  const shoppingBagDrawerRef = useRef<HTMLElement | null>(null);
  const shoppingBagCloseRef = useRef<HTMLButtonElement | null>(null);
  const accountPointerRef = useRef({ x: Number.NaN, y: Number.NaN });

  const clearRegionTimer = () => {
    if (!regionCloseTimer.current) return;
    clearTimeout(regionCloseTimer.current);
    regionCloseTimer.current = null;
  };

  const clearAccountTimer = () => {
    if (!accountCloseTimer.current) return;
    clearTimeout(accountCloseTimer.current);
    accountCloseTimer.current = null;
  };

  const pointIsInside = (element: HTMLElement | null, x: number, y: number) => {
    if (!element || !Number.isFinite(x) || !Number.isFinite(y)) return false;
    const bounds = element.getBoundingClientRect();
    return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
  };

  const accountSurfaceIsActive = () => {
    const region = accountHoverRegionRef.current;
    const dropdown = accountDropdownRef.current;
    const bridge = accountBridgeRef.current;
    const activeElement = document.activeElement;
    const { x, y } = accountPointerRef.current;

    return Boolean(
      region?.matches(":hover") ||
      dropdown?.matches(":hover") ||
      bridge?.matches(":hover") ||
      (activeElement && dropdown?.contains(activeElement)) ||
      pointIsInside(region, x, y) ||
      pointIsInside(dropdown, x, y) ||
      pointIsInside(bridge, x, y)
    );
  };

  const keepAccountMenuOpen = (event: MouseEvent<HTMLElement>) => {
    accountPointerRef.current = { x: event.clientX, y: event.clientY };
    clearAccountTimer();
  };

  useEffect(() => {
    if (!accountMenuOpen) return;

    const trackPointerAcrossAccountSurface = (event: PointerEvent) => {
      accountPointerRef.current = { x: event.clientX, y: event.clientY };
      const { x, y } = accountPointerRef.current;
      if (
        pointIsInside(accountHoverRegionRef.current, x, y) ||
        pointIsInside(accountDropdownRef.current, x, y) ||
        pointIsInside(accountBridgeRef.current, x, y)
      ) {
        if (accountCloseTimer.current) {
          clearTimeout(accountCloseTimer.current);
          accountCloseTimer.current = null;
        }
      }
    };

    document.addEventListener("pointermove", trackPointerAcrossAccountSurface, true);
    return () => document.removeEventListener("pointermove", trackPointerAcrossAccountSurface, true);
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!accountMenuOpen) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && accountHoverRegionRef.current?.contains(target)) return;
      if (accountCloseTimer.current) {
        clearTimeout(accountCloseTimer.current);
        accountCloseTimer.current = null;
      }
      setAccountMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
  }, [accountMenuOpen]);

  const openLocalePicker = () => {
    clearRegionTimer();
    clearAccountTimer();
    setAccountMenuOpen(false);
    setShoppingBagOpen(false);
    setRegionOpen(true);
  };

  const closeLocalePickerSoon = () => {
    clearRegionTimer();
    regionCloseTimer.current = setTimeout(() => {
      setRegionOpen(false);
      regionCloseTimer.current = null;
    }, 260);
  };

  const openAccountMenu = () => {
    clearAccountTimer();
    clearRegionTimer();
    setRegionOpen(false);
    setShoppingBagOpen(false);
    setAccountMenuOpen(true);
  };

  const closeAccountMenuSoon = (event: MouseEvent<HTMLElement>) => {
    accountPointerRef.current = { x: event.clientX, y: event.clientY };
    clearAccountTimer();
    const closeWhenPointerIsOutside = () => {
      if (accountSurfaceIsActive()) {
        accountCloseTimer.current = setTimeout(closeWhenPointerIsOutside, 120);
        return;
      }

      setAccountMenuOpen(false);
      accountCloseTimer.current = null;
    };

    accountCloseTimer.current = setTimeout(closeWhenPointerIsOutside, 420);
  };

  const loadShoppingBag = useCallback(async () => {
    setShoppingBagStatus("loading");
    setShoppingBagError("");

    try {
      const response = await fetch("/api/cart", { cache: "no-store" });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok || !Array.isArray(payload.items)) {
        throw new Error(response.status === 401 ? "Sign in to view your shopping bag." : "Your shopping bag could not be loaded.");
      }

      setShoppingBagItems(payload.items as HeaderCartItem[]);
      setShoppingBagStatus("ready");
    } catch (error) {
      setShoppingBagItems([]);
      setShoppingBagError(error instanceof Error ? error.message : "Your shopping bag could not be loaded.");
      setShoppingBagStatus("error");
    }
  }, []);

  const toggleShoppingBag = () => {
    clearRegionTimer();
    clearAccountTimer();
    setRegionOpen(false);
    setAccountMenuOpen(false);
    setShoppingBagOpen((open) => {
      if (!open) void loadShoppingBag();
      return !open;
    });
  };

  const updateShoppingBagQuantity = async (item: HeaderCartItem, quantity: number) => {
    if (quantity < 1 || quantity > 999 || shoppingBagBusyIds.includes(item.id)) return;
    setShoppingBagBusyIds((current) => [...current, item.id]);
    setShoppingBagError("");
    try {
      const response = await fetch(`/api/cart/${encodeURIComponent(item.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity }),
      });
      if (!response.ok) throw new Error("Quantity could not be updated.");
      await loadShoppingBag();
    } catch (error) {
      setShoppingBagError(error instanceof Error ? error.message : "Quantity could not be updated.");
    } finally {
      setShoppingBagBusyIds((current) => current.filter((id) => id !== item.id));
    }
  };

  const removeShoppingBagItem = async (item: HeaderCartItem) => {
    if (shoppingBagBusyIds.includes(item.id)) return;
    setShoppingBagBusyIds((current) => [...current, item.id]);
    setShoppingBagError("");
    try {
      const response = await fetch(`/api/cart/${encodeURIComponent(item.id)}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Item could not be removed.");
      await loadShoppingBag();
    } catch (error) {
      setShoppingBagError(error instanceof Error ? error.message : "Item could not be removed.");
    } finally {
      setShoppingBagBusyIds((current) => current.filter((id) => id !== item.id));
    }
  };

  const navigateFromHeader = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    setMenuOpen(false);
    setRegionOpen(false);
    setAccountMenuOpen(false);
    setShoppingBagOpen(false);
    window.location.assign(href);
  };

  useEffect(() => {
    return () => {
      clearRegionTimer();
      clearAccountTimer();
    };
  }, []);

  useEffect(() => {
    const syncSession = () => setAuthSession(readAuthSession());
    const syncCart = () => window.queueMicrotask(() => void loadShoppingBag());
    if (initialSession) writeAuthSession(initialSession);
    else syncSession();
    syncCart();
    ensureFreshAuthSession().catch(() => undefined);
    const syncAccountData = () => {
      syncSession();
      syncCart();
    };
    window.addEventListener("storage", syncAccountData);
    window.addEventListener(AUTH_CHANGE_EVENT, syncAccountData);
    return () => {
      window.removeEventListener("storage", syncAccountData);
      window.removeEventListener(AUTH_CHANGE_EVENT, syncAccountData);
    };
  }, [initialSession, loadShoppingBag]);

  useEffect(() => {
    if (!mobileLayers) return;
    const openSearch = (event: Event) => {
      const mode =
        event instanceof CustomEvent && event.detail?.mode === "image" ? "image" : "product";
      setMenuOpen(false);
      setMobileSearchMode(mode);
      setMobileSearchOpen(true);
    };

    window.addEventListener("prvbuy:open-mobile-search", openSearch);
    return () => window.removeEventListener("prvbuy:open-mobile-search", openSearch);
  }, [mobileLayers]);

  useEffect(() => {
    if (!menuOpen && !accountMenuOpen && !shoppingBagOpen && !guideOpen && !mobileSearchOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setAccountMenuOpen(false);
        setShoppingBagOpen(false);
        setGuideOpen(false);
        setMobileSearchOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [accountMenuOpen, guideOpen, menuOpen, mobileSearchOpen, shoppingBagOpen]);

  useEffect(() => {
    if (!menuOpen && !shoppingBagOpen && !guideOpen && !mobileSearchOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [guideOpen, menuOpen, mobileSearchOpen, shoppingBagOpen]);

  useEffect(() => {
    if (!shoppingBagOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const opener = shoppingBagButtonRef.current;
    shoppingBagCloseRef.current?.focus();

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const drawer = shoppingBagDrawerRef.current;
      if (!drawer) return;
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      (opener ?? previousFocus)?.focus();
    };
  }, [shoppingBagOpen]);

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 50);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const styleId = "current-morphic-header-lock-style";
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }

    style.textContent = morphicHeaderLockCss;

    return () => {
      style?.remove();
    };
  }, []);

  const shoppingBagCount = shoppingBagItems.reduce((sum, item) => sum + item.quantity, 0);
  const shoppingBagSubtotal = summarizeCartItems(shoppingBagItems);
  const shoppingBagCheckoutHref = shoppingBagItems.length > 0
    ? `/checkout?cartItemIds=${shoppingBagItems.map((item) => encodeURIComponent(item.id)).join(",")}`
    : "/cart";

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: morphicHeaderLockCss }} />
      <header
        id="prvbuy-global-header"
        className={`cd-header home-shared-header${headerScrolled ? " cd-header-scrolled" : ""}`}
      >
        <div className="current-morphic-nav-blur" aria-hidden="true" />
        <nav className="cd-nav" aria-label="Primary navigation">
          <Link className="brand-pill" href="/" aria-label="PRVBUY home" onClick={(event) => navigateFromHeader(event, "/")}>
            <span className="brand-logo-frame" aria-hidden="true">
              <Image
                src="/images/prvbuy-logo-p-flat.png"
                alt=""
                width={72}
                height={72}
                className="brand-logo-img"
              />
            </span>
            <span>PRVBUY</span>
          </Link>

          {mobileLayers ? (
            <button
              className="mobile-sticky-link-bar"
              type="button"
              aria-label="Open product link or keyword search"
              onClick={() => {
                setMobileSearchMode("product");
                setMobileSearchOpen(true);
              }}
            >
              <Link2 size={16} aria-hidden="true" />
              <span>{linkPlaceholder}</span>
              <ArrowRight size={17} aria-hidden="true" />
            </button>
          ) : null}

          <form
            className="header-link-entry"
            role="search"
            action="/product/search"
            method="get"
            onSubmit={(event) => {
              event.preventDefault();
              if (!headerLink.trim()) return;
              window.location.assign(productSearchDestination(headerLink.trim(), readLocalePreferences().language));
            }}
          >
            <Link2 size={15} aria-hidden="true" />
            <input
              type="text"
              name="q"
              value={headerLink}
              onChange={(event) => setHeaderLink(event.target.value)}
              placeholder={linkPlaceholder}
              aria-label={linkPlaceholder}
            />
            <button type="submit" aria-label="Open product link">
              <ArrowRight size={15} />
            </button>
          </form>

          <div
            className="nav-pill liquid-pill"
            data-active={activeNav}
            style={{ "--nav-active": activeNav } as CSSProperties}
          >
            {navItems.map((item) => (
              <Link href={item.href} key={item.href} onClick={(event) => navigateFromHeader(event, item.href)}>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="header-tools">

          <details
            className="locale-picker"
            open={regionOpen}
            onMouseEnter={openLocalePicker}
            onMouseLeave={closeLocalePickerSoon}
          >
            <summary
              className="region-pill"
              aria-label="Language and currency"
              onClick={(event) => {
                event.preventDefault();
                clearRegionTimer();
                setAccountMenuOpen(false);
                setRegionOpen(true);
              }}
              onFocus={openLocalePicker}
            >
              <Globe2 size={18} />
              <span>{localeLanguage}</span>
              <span className="region-currency">{localeCurrency}</span>
              <ChevronDown size={18} />
            </summary>

            <div
              className="locale-popover"
              role="dialog"
              aria-label="Language and currency selector"
              onMouseEnter={clearRegionTimer}
              onMouseLeave={closeLocalePickerSoon}
            >
                <div
                  className="locale-hover-bridge"
                  aria-hidden="true"
                  onMouseEnter={clearRegionTimer}
                />
                <div className="locale-popover-arrow" />
                <header className="locale-popover-head">
                  <div>
                    <strong>Language &amp; currency</strong>
                    <span>Choose how PRVBUY is displayed</span>
                  </div>
                  <span className="locale-current">{localeLanguage} · {localeCurrency}</span>
                </header>
                <div className="locale-popover-columns">
                <section className="locale-section">
                  <div className="locale-heading">Language</div>
                  <div className="locale-grid language-grid">
                    {designedLanguages.map((language) => (
                      <button
                        type="button"
                        className={`locale-option${language.code === localeLanguage ? " active" : language.available ? "" : " unavailable"}`}
                        aria-current={language.code === localeLanguage ? "true" : undefined}
                        aria-disabled={language.available ? undefined : "true"}
                        disabled={!language.available}
                        onClick={() => language.available && selectLanguage(language.code)}
                        title={language.code === localeLanguage ? "Current language" : language.available ? `Use ${language.label}` : `${language.label} · Coming Soon`}
                        key={language.code}
                      >
                        {language.label}
                      </button>
                    ))}
                  </div>
                </section>
                <section className="locale-section">
                  <div className="locale-heading">Currency</div>
                  <div className="locale-grid currency-grid">
                    {designedCurrencies.map((currency) => {
                      const available = currency === supportedLocale.currency;
                      return (
                        <button
                          type="button"
                          className={`locale-option${available ? " active" : " unavailable"}`}
                          aria-current={available ? "true" : undefined}
                          aria-disabled="true"
                          disabled
                          title={available ? "Current currency" : `${currency} · Coming Soon`}
                          key={currency}
                        >
                          {currency}
                        </button>
                      );
                    })}
                  </div>
                </section>
                </div>
            </div>
          </details>

          {authSession ? (
            <div
              ref={accountHoverRegionRef}
              className="auth-pill auth-pill-signed-in"
              aria-label="Signed in account preview"
              onMouseEnter={openAccountMenu}
              onMouseLeave={closeAccountMenuSoon}
              onMouseMove={keepAccountMenuOpen}
              onFocusCapture={clearAccountTimer}
              onBlurCapture={(event) => {
                const nextTarget = event.relatedTarget;
                if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
                clearAccountTimer();
                setAccountMenuOpen(false);
              }}
            >
              <button
                className="account-avatar-button"
                type="button"
                aria-label="Open account menu"
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                onClick={openAccountMenu}
                onFocus={openAccountMenu}
              >
                <span className="user-avatar" aria-hidden="true">
                  {authSession.name.charAt(0).toUpperCase()}
                </span>
              </button>

              {accountMenuOpen ? (
                <div
                  ref={accountDropdownRef}
                  className="account-dropdown"
                  role="menu"
                  aria-label="Account menu"
                  onMouseEnter={clearAccountTimer}
                  onMouseMove={keepAccountMenuOpen}
                >
                  <div
                    ref={accountBridgeRef}
                    className="account-hover-bridge"
                    aria-hidden="true"
                    onMouseEnter={clearAccountTimer}
                    onMouseMove={keepAccountMenuOpen}
                  />
                  <div className="account-menu-identity">
                    <strong>{authSession.name}</strong>
                    <span>{authSession.email}</span>
                  </div>
                  <div className="account-menu-links">
                    {portalNav.map((item) => {
                      const active = isPortalNavItemActive(pathname, item);
                      return (
                        <Link
                          href={item.href}
                          key={item.href}
                          role="menuitem"
                          aria-current={active ? "page" : undefined}
                          className={[
                            active ? accountNavStyles.activeLink : "",
                            item.groupStart ? accountNavStyles.groupStart : "",
                          ].filter(Boolean).join(" ") || undefined}
                        >
                          <AccountMenuLinkContent
                            label={item.label}
                            comingSoon={item.status === "coming-soon"}
                          />
                        </Link>
                      );
                    })}
                  </div>
                  <button
                    className="account-menu-signout"
                    type="button"
                    role="menuitem"
                    disabled={isSigningOut}
                    aria-busy={isSigningOut}
                    onClick={() => {
                      setAccountMenuOpen(false);
                      void signOut();
                    }}
                  >
                    {isSigningOut ? "Logging Out…" : "Log Out"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className="auth-pill liquid-pill"
              aria-label="Account actions"
              data-active={1}
              style={{ "--auth-active": 1 } as CSSProperties}
            >
              <Link href="/login" onClick={(event) => navigateFromHeader(event, "/login")}>
                Log in
              </Link>
              <Link href="/register" onClick={(event) => navigateFromHeader(event, "/register")}>
                Sign up
              </Link>
            </div>
          )}

          <div className="shopping-bag-trigger">
            <button
              ref={shoppingBagButtonRef}
              className="cart-pill"
              type="button"
              aria-label="Open shopping bag"
              aria-haspopup="dialog"
              aria-expanded={shoppingBagOpen}
              onClick={toggleShoppingBag}
            >
              <ShoppingBag size={21} />
              {shoppingBagCount > 0 ? (
                <span className="shopping-bag-count" aria-label={`${shoppingBagCount} items in shopping bag`}>
                  {shoppingBagCount}
                </span>
              ) : null}
            </button>
          </div>
          </div>

          <button
            className="menu-pill"
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="prv-mobile-account-drawer"
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X size={25} /> : <Menu size={25} />}
          </button>
        </nav>

        {mobileLayers ? (
          <>
            <MobileAccountDrawer
              open={menuOpen}
              session={authSession}
              pathname={pathname}
              onClose={() => setMenuOpen(false)}
              onOpenSearch={(mode) => {
                setMobileSearchMode(mode);
                setMobileSearchOpen(true);
              }}
              onOpenGuide={() => setGuideOpen(true)}
              onSignOut={() => {
                setMenuOpen(false);
                void signOut();
              }}
              signingOut={isSigningOut}
            />

          </>
        ) : null}
      </header>

      {mobileLayers ? (
        <MobileProductSearch
          open={mobileSearchOpen}
          initialMode={mobileSearchMode}
          placeholder={linkPlaceholder}
          onClose={() => setMobileSearchOpen(false)}
        />
      ) : null}

      {shoppingBagOpen ? (
        <div className="shopping-bag-sheet prvbuy-shopping-bag-sheet" role="dialog" aria-modal="true" aria-labelledby="prvbuy-shopping-bag-title">
          <button
            className="shopping-bag-backdrop"
            type="button"
            aria-label="Close shopping bag"
            onClick={() => setShoppingBagOpen(false)}
          />
          <aside ref={shoppingBagDrawerRef} className="shopping-bag-drawer">
            <div className="shopping-bag-drawer-head">
              <div>
                <span className="shopping-bag-kicker">PRVBUY bag</span>
                <h2 id="prvbuy-shopping-bag-title">Shopping bag</h2>
                <p aria-live="polite">
                  {shoppingBagStatus === "loading"
                    ? "Loading your saved items"
                    : `${shoppingBagCount} item${shoppingBagCount === 1 ? "" : "s"} in your bag`}
                </p>
              </div>
              <button
                ref={shoppingBagCloseRef}
                className="shopping-bag-close"
                type="button"
                aria-label="Close shopping bag"
                onClick={() => setShoppingBagOpen(false)}
              >
                <X size={28} />
              </button>
            </div>
            <div className="shopping-bag-drawer-body" aria-live="polite">
              {shoppingBagStatus === "loading" ? (
                <div className="shopping-bag-empty" role="status">
                  <strong>Loading your bag</strong>
                  <span>Checking your current account&apos;s saved cart items.</span>
                </div>
              ) : shoppingBagStatus === "error" ? (
                <div className="shopping-bag-empty shopping-bag-error" role="alert">
                  <strong>Bag unavailable</strong>
                  <span>{shoppingBagError}</span>
                  <button type="button" className="shopping-bag-retry" onClick={() => void loadShoppingBag()}>
                    Retry
                  </button>
                </div>
              ) : shoppingBagItems.length === 0 ? (
                <div className="shopping-bag-empty">
                  <strong>Your bag is empty</strong>
                  <span>Add a real product before continuing to Checkout.</span>
                </div>
              ) : (
                shoppingBagItems.map((item) => {
                  const busy = shoppingBagBusyIds.includes(item.id);
                  return (
                    <article className="shopping-bag-row" key={item.id} aria-busy={busy}>
                      <div className="shopping-bag-product-image">
                        <Image src={item.image} alt="" width={176} height={176} unoptimized />
                      </div>
                      <div className="shopping-bag-product-main">
                        <strong>{item.title}</strong>
                        <span>{item.skuLabel}</span>
                        <small>{item.sellerName}{item.platform ? ` · ${item.platform}` : ""}</small>
                        <div className="shopping-bag-quantity" aria-label={`Quantity for ${item.title}`}>
                          <button
                            type="button"
                            aria-label={`Decrease ${item.title} quantity`}
                            disabled={busy || item.quantity <= 1}
                            onClick={() => void updateShoppingBagQuantity(item, item.quantity - 1)}
                          >
                            <Minus size={14} />
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            type="button"
                            aria-label={`Increase ${item.title} quantity`}
                            disabled={busy || item.quantity >= 999}
                            onClick={() => void updateShoppingBagQuantity(item, item.quantity + 1)}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="shopping-bag-product-side">
                        <button
                          type="button"
                          aria-label={`Remove ${item.title}`}
                          disabled={busy}
                          onClick={() => void removeShoppingBagItem(item)}
                        >
                          <X size={22} />
                        </button>
                        <strong>{formatCartMoney(item.unitPrice.currency, item.unitPrice.amount * item.quantity)}</strong>
                      </div>
                    </article>
                  );
                })
              )}
              {shoppingBagStatus === "ready" && shoppingBagError ? (
                <p className="shopping-bag-operation-error" role="alert">{shoppingBagError}</p>
              ) : null}
            </div>
            <div className="shopping-bag-summary">
              <div>
                <span>Items subtotal</span>
                <strong>{shoppingBagSubtotal || "—"}</strong>
              </div>
              <p className="shopping-bag-summary-note">
                Domestic and international shipping remain subject to the existing Checkout and parcel contracts.
              </p>
              <Link className="shopping-bag-view" href="/cart" onClick={() => setShoppingBagOpen(false)}>
                View Bag
              </Link>
              {shoppingBagStatus === "ready" && shoppingBagItems.length > 0 ? (
                <Link className="shopping-bag-checkout" href={shoppingBagCheckoutHref} onClick={() => setShoppingBagOpen(false)}>
                  Checkout
                </Link>
              ) : (
                <button className="shopping-bag-checkout" type="button" disabled>
                  Checkout
                </button>
              )}
            </div>
          </aside>
        </div>
      ) : null}

      <aside className="prvbuy-floating-tools" aria-label="PRVBUY quick help">
        <Link className="prvbuy-floating-tool" href="/user-center/support" aria-label="Customer support">
          <span className="prvbuy-floating-tool-icon" aria-hidden="true">
            <Headphones size={23} strokeWidth={1.8} />
          </span>
          <span className="prvbuy-floating-tool-label">Customer support</span>
        </Link>
        <a
          className="prvbuy-floating-tool"
          href={discordLink.href ?? "#"}
          aria-label={discordLink.href ? "PRVBUY Discord" : "PRVBUY Discord coming soon"}
          aria-disabled={discordLink.href ? undefined : true}
          target={discordLink.href ? "_blank" : undefined}
          rel={discordLink.href ? "noopener noreferrer" : undefined}
          onClick={discordLink.href ? undefined : (event) => event.preventDefault()}
        >
          <span className="prvbuy-floating-tool-icon" aria-hidden="true">
            <Image src="/images/footer/social-discord.svg" alt="" width={25} height={25} />
          </span>
          <span className="prvbuy-floating-tool-label">Join our Discord</span>
        </a>
        <a
          className="prvbuy-floating-tool"
          href={redditLink.href ?? "#"}
          aria-label={redditLink.href ? "PRVBUY Reddit" : "PRVBUY Reddit coming soon"}
          aria-disabled={redditLink.href ? undefined : true}
          target={redditLink.href ? "_blank" : undefined}
          rel={redditLink.href ? "noopener noreferrer" : undefined}
          onClick={redditLink.href ? undefined : (event) => event.preventDefault()}
        >
          <span className="prvbuy-floating-tool-icon" aria-hidden="true">
            <Image src="/images/footer/social-reddit.svg" alt="" width={25} height={25} />
          </span>
          <span className="prvbuy-floating-tool-label">Visit our Reddit</span>
        </a>
      </aside>

      <button
        className="prvbuy-guide-launcher"
        type="button"
        aria-label="Open the PRVBUY YouTube guide"
        aria-haspopup="dialog"
        aria-expanded={guideOpen}
        onClick={() => setGuideOpen(true)}
      >
        <span className="prvbuy-guide-launcher-visual" aria-hidden="true">
          <Play size={22} fill="currentColor" />
        </span>
        <span className="prvbuy-guide-launcher-copy">
          <strong>How PRVBUY Works</strong>
          <span>YouTube guide</span>
        </span>
      </button>

      {guideOpen ? (
        <div
          className="prvbuy-guide-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prvbuy-guide-title"
        >
          <button
            className="prvbuy-guide-backdrop"
            type="button"
            aria-label="Close PRVBUY guide"
            onClick={() => setGuideOpen(false)}
          />
          <section className="prvbuy-guide-dialog">
            <header className="prvbuy-guide-dialog-head">
              <div>
                <span>PRVBUY guide</span>
                <h2 id="prvbuy-guide-title">How to buy with PRVBUY</h2>
              </div>
              <button
                className="prvbuy-guide-close"
                type="button"
                aria-label="Close guide"
                onClick={() => setGuideOpen(false)}
              >
                <X size={21} />
              </button>
            </header>
            <div className="prvbuy-guide-media">
              <Image
                src="/images/hero-prvbuy-workspace.png"
                alt="PRVBUY buying and warehouse guide preview"
                width={1800}
                height={964}
                sizes="(max-width: 720px) calc(100vw - 24px), 900px"
                priority
              />
              <span className="prvbuy-guide-play" aria-hidden="true">
                <Play size={32} fill="currentColor" />
              </span>
            </div>
            <footer className="prvbuy-guide-dialog-foot">
              <p>Paste a product link, review recorded QC photos, combine eligible items and build a parcel.</p>
              <a
                className="prvbuy-guide-youtube"
                href={youtubeLink.href ?? "#"}
                aria-disabled={youtubeLink.href ? undefined : true}
                target={youtubeLink.href ? "_blank" : undefined}
                rel={youtubeLink.href ? "noopener noreferrer" : undefined}
                onClick={youtubeLink.href ? undefined : (event) => event.preventDefault()}
              >
                Watch on YouTube
                <ExternalLink size={14} />
              </a>
            </footer>
          </section>
        </div>
      ) : null}

    </>
  );
}
